import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Fab,
  Tooltip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Switch,
  FormControlLabel,
  Divider,
  Tabs,
  Tab,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as ContentCopyIcon,
  Close as CloseIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  FolderOpen as FolderOpenIcon,
  Visibility as VisibilityIcon,
  DriveFileMove as DriveFileMoveIcon,
  PlayArrow as PlayArrowIcon
} from '@mui/icons-material';
import { getAuthToken } from '../utils/auth';
import ImageGallery from './ImageGallery';

const apiUrl = process.env.REACT_APP_API_URL;

interface Newsletter {
  id: string;
  title: string;
  status: string;
  author: string;
  createdAt: string;
  updatedAt: string;
  audienceVariation?: string;
  newsletterType?: string;
}

interface FooterLink {
  linkText: string;
  linkURL: string;
}

interface Article {
  title: string;
  url: string;
  imageUrl?: string;
  selected?: boolean;
}

interface VaultArticle {
  title: string;
  url: string;
  imageUrl?: string;
  selected: boolean;
}

interface ReadOnlyArticle {
  title: string;
  url: string;
  imageUrl?: string;
  selected: boolean;
}

interface Ad {
  imageUrl: string;
  adLink: string;
  position: number;
}

interface NewsletterManagementProps {
  currentUser?: {
    id: string;
    username: string;
    firstName: string;
    lastName: string;
    role: string;
    isActive: boolean;
  } | null;
  themeMode?: 'light' | 'dark';
  filteredNewsletters?: Newsletter[];
  selectedFolder?: string | null;
  selectedFolderName?: string | null;
  onNewsletterCreated?: () => void;
}

const CAR_AND_DRIVER_SYNC_AUDIENCE = 'newsletter-carand-driver';
const CAR_AND_DRIVER_SYNC_NAME = 'Car And Driver NewsLetter Sync';

const ALL_AUDIENCE_OPTIONS = [
  'Newsletter_MotorTrend',
  'Newsletter_HotRod',
  'Newsletter_Events_MotorTrend',
  'Newsletter_Events_HotRod',
  'rateyourcar_newsletter',
  'reengagement_newsletter',
  'automate_rating_reengagement_email',
  'automate_vehicle_selection_reengagement_email',
  'review_reengagement_newsletter',
  'automate_review_reengagement_email',
  'select_vehicle_reengagement_newsletter',
  CAR_AND_DRIVER_SYNC_AUDIENCE,
  'test_rateyourcar_newsletter',
  'test_reengagement_newsletter',
  'test_review_reengagement_newsletter',
  'test_select_vehicle_reengagement_newsletter',
  'test_hotrod_newsletter',
  'test_motortrend_newsletter',
  'test_events_motortrend_newsletter',
  'test_events_hotrod_newsletter'
];

const TEST_AUDIENCE_OPTIONS = [
  'test_hotrod_newsletter',
  'test_motortrend_newsletter',
  'test_events_motortrend_newsletter',
  'test_events_hotrod_newsletter',
  'test_rateyourcar_newsletter',
  'test_reengagement_newsletter',
  'test_review_reengagement_newsletter',
  'automate_rating_reengagement_email',
  'automate_review_reengagement_email',
  'automate_vehicle_selection_reengagement_email',
  'test_select_vehicle_reengagement_newsletter',
  CAR_AND_DRIVER_SYNC_AUDIENCE
];

const RATE_YOUR_CAR_SEND_DAY_OPTIONS = Array.from({ length: 30 }, (_, index) => index + 1);
const isProductionStage = ['prod', 'production'].includes((process.env.REACT_APP_STAGE || '').toLowerCase());

// Determine environment and filter audience options accordingly
const getAudienceOptions = () => {
  if (isProductionStage) {
    return ALL_AUDIENCE_OPTIONS;
  }
  return TEST_AUDIENCE_OPTIONS;
};

const NEWSLETTERS_BASE_URL = 'https://www.motortrend.com/newsletters';

// Map audience ID to newsletter type key for backend config
const getNewsletterTypeFromAudience = (audience: string): string => {
  const audienceToTypeMap: { [key: string]: string } = {
    'Newsletter_MotorTrend': 'motortrend',
    'Newsletter_HotRod': 'hotrod',
    'Newsletter_Events_MotorTrend': 'events_motortrend',
    'Newsletter_Events_HotRod': 'events_hotrod',
    'rateyourcar_newsletter': 'rateyourcar_newsletter',
    'reengagement_newsletter': 'reengagement_newsletter',
    'automate_rating_reengagement_email': 'automate_rating_reengagement_email',
    'review_reengagement_newsletter': 'review_reengagement_newsletter',
    'automate_review_reengagement_email': 'automate_review_reengagement_email',
    'select_vehicle_reengagement_newsletter': 'select_vehicle_reengagement_newsletter',
    [CAR_AND_DRIVER_SYNC_AUDIENCE]: CAR_AND_DRIVER_SYNC_AUDIENCE,
    'test_rateyourcar_newsletter': 'test_rateyourcar_newsletter',
    'test_reengagement_newsletter': 'test_reengagement_newsletter',
    'test_review_reengagement_newsletter': 'test_review_reengagement_newsletter',
    'test_select_vehicle_reengagement_newsletter': 'test_select_vehicle_reengagement_newsletter',
    'test_hotrod_newsletter': 'test_hotrod_newsletter',
    'test_motortrend_newsletter': 'test_motortrend_newsletter',
    'test_events_motortrend_newsletter': 'test_events_motortrend_newsletter',
    'test_events_hotrod_newsletter': 'test_events_hotrod_newsletter'
  };

  return audienceToTypeMap[audience] || audience;
};

const isEventsAudienceValue = (audience: string): boolean => {
  return (
    audience === 'Newsletter_Events_MotorTrend' ||
    audience === 'Newsletter_Events_HotRod' ||
    audience === 'test_events_motortrend_newsletter' ||
    audience === 'test_events_hotrod_newsletter'
  );
};

const isRateYourCarAudienceValue = (audience: string): boolean => {
  return audience === 'rateyourcar_newsletter' || audience === 'test_rateyourcar_newsletter';
};

const isReEngagementAudienceValue = (audience: string): boolean => {
  return (
    audience === 'reengagement_newsletter' ||
    audience === 'test_reengagement_newsletter' ||
    audience === 'automate_rating_reengagement_email' ||
    audience === 'automate_vehicle_selection_reengagement_email' ||
    audience === 'review_reengagement_newsletter' ||
    audience === 'test_review_reengagement_newsletter' ||
    audience === 'automate_review_reengagement_email' ||
    audience === 'select_vehicle_reengagement_newsletter' ||
    audience === 'test_select_vehicle_reengagement_newsletter'
  );
};

const isDailyOnlyReEngagementAudienceValue = (audience: string): boolean => (
  isProductionStage && (
    audience === 'automate_rating_reengagement_email' ||
    audience === 'automate_review_reengagement_email' ||
    audience === 'automate_vehicle_selection_reengagement_email'
  )
);

const isManuallySchedulableReEngagementAudienceValue = (audience: string): boolean => (
  !isProductionStage && (
    audience === 'automate_rating_reengagement_email' ||
    audience === 'automate_review_reengagement_email' ||
    audience === 'automate_vehicle_selection_reengagement_email'
  )
);

const isReviewReEngagementAudienceValue = (audience: string): boolean => (
  audience === 'review_reengagement_newsletter' ||
  audience === 'test_review_reengagement_newsletter' ||
  audience === 'automate_review_reengagement_email'
);

const isConfigurableReviewReEngagementAudienceValue = (audience: string): boolean => (
  audience === 'review_reengagement_newsletter' ||
  audience === 'test_review_reengagement_newsletter'
);

const isCarAndDriverSyncAudienceValue = (audience: string): boolean => {
  return audience === CAR_AND_DRIVER_SYNC_AUDIENCE;
};

const NEWSLETTER_FORM_TABS = [
  { value: 0, label: 'Header' },
  { value: 1, label: 'Content' },
  { value: 2, label: 'Ads' },
  { value: 3, label: 'Footer' },
  { value: 4, label: 'Schedule' }
];

// Default footer text for Hot Rod newsletters
const HOT_ROD_FOOTER_TEXT = `HOT ROD is a publication of Hearst Autos, Inc.
2026 Hearst Autos, Inc. All Rights Reserved.
This email was sent by HOT ROD, a publication of Hearst Autos, Inc., 300 West 57th Street, New York, NY, 10019-3779`;

// Default footer text for MotorTrend newsletters
const MOTORTREND_FOOTER_TEXT = `MotorTrend is a publication of Hearst Autos, Inc.
2026 Hearst Autos, Inc. All Rights Reserved.
This email was sent by MotorTrend, a publication of Hearst Autos, Inc., 300 West 57th Street, New York, NY, 10019-3779`;

// Default footer text for other newsletters
const DEFAULT_FOOTER_TEXT = 'Copyright 2026 Motor Trend Group, All Rights Reserved.\nWrite to us at motortrend@motortrend.com or 2004 Park Place, Suite K, El Segundo, CA 90245';

// Default footer links for Hot Rod newsletters
const HOT_ROD_FOOTER_LINKS = [
  { linkText: 'Unsubscribe', linkURL: `${NEWSLETTERS_BASE_URL}/` },
  { linkText: 'Privacy Notice', linkURL: 'https://www.hearst.com/-/us-magazines-privacy-notice' },
  { linkText: 'CA Notice of Cancellation', linkURL: 'https://www.hearst.com/-/us-magazines-privacy-notice#_ADDITIONAL_INFO' }
];

// Default footer links for MotorTrend newsletters
const MOTORTREND_FOOTER_LINKS = [
  { linkText: 'Unsubscribe', linkURL: `${NEWSLETTERS_BASE_URL}/` },
  { linkText: 'Privacy Notice', linkURL: 'https://www.hearst.com/-/us-magazines-privacy-notice' },
  { linkText: 'CA Notice of Cancellation', linkURL: 'https://www.hearst.com/-/us-magazines-privacy-notice#_ADDITIONAL_INFO' }
];

// Default footer links for other newsletters
const DEFAULT_FOOTER_LINKS = [
  { linkText: 'Privacy Policy', linkURL: 'https://www.motortrend.com/privacy-policy/' },
  { linkText: 'California Privacy Policy', linkURL: 'https://www.motortrend.com/ca-privacy-policy/' },
  { linkText: 'Unsubscribe', linkURL: 'https://mtg-newsletter-unsubscribe.vercel.app?id={emailid}' }
];

// Default header logo for MotorTrend and Events newsletters
const MOTORTREND_HEADER_LOGO = 'https://d29997cmjxtl4j.cloudfront.net/images/motortrendlogo-1 (1).png';

// Default header logo for Hot Rod newsletters
const HOT_ROD_HEADER_LOGO = 'https://d29997cmjxtl4j.cloudfront.net/images/hotrod.png';

// Default header text for MotorTrend and Events newsletters
const MOTORTREND_HEADER_TEXT = 'Your No. 1 source for auto news, reviews, and more.';

// Default header text for Hot Rod newsletters
const HOT_ROD_HEADER_TEXT = 'Where it all began. Where it\'s all going. Since 1948.';

const NewsletterManagement: React.FC<NewsletterManagementProps> = ({ currentUser, themeMode = 'light', filteredNewsletters, selectedFolder, selectedFolderName, onNewsletterCreated }) => {
  const [newsletters, setNewsletters] = useState<Newsletter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [triggeringCampaignId, setTriggeringCampaignId] = useState<string | null>(null);

  // Create newsletter dialog state
  const [createDialog, setCreateDialog] = useState(false);
  const [keepDialogOpen, setKeepDialogOpen] = useState(false);
  const keepDialogOpenRef = useRef(false);
  const [activeTab, setActiveTab] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editingNewsletterId, setEditingNewsletterId] = useState<string | null>(null);
  const [newNewsletter, setNewNewsletter] = useState({
    // Basic Info
    audience: '',
    newsletterName: '',
    subject: '',
    enableLlmSubjectLine: true,
    previewText: '',
    status: 'draft',
    
    // Header
    headerLogo: '',
    headerText: '',
    
    // Footer
    footerText: DEFAULT_FOOTER_TEXT,
    footerLinks: DEFAULT_FOOTER_LINKS as FooterLink[],
    
    // Content
    articles: [{ title: '', url: '', imageUrl: '', selected: true }],
    featuredArticlesEnabled: true,
    
    // Vault Articles
    vaultArticles: [
      { title: '', url: '', imageUrl: '', selected: false }
    ] as VaultArticle[],
    vaultArticlesEnabled: false,
    
    // Latest Articles
    latestArticles: [] as ReadOnlyArticle[],
    latestArticlesEnabled: false,
    
    // Recommended For You
    recommendedArticles: [] as ReadOnlyArticle[],
    recommendedArticlesEnabled: false,
    
    // You May Also Like
    youMayAlsoLikeArticles: [] as ReadOnlyArticle[],
    youMayAlsoLikeArticlesEnabled: false,
    
    // Trending Articles
    trendingArticles: [] as ReadOnlyArticle[],
    trendingArticlesEnabled: false,
    
    // Trending Videos
    trendingVideos: [] as ReadOnlyArticle[],
    trendingVideosEnabled: false,
    
    // Text Content
    textContent: '',
    textContentEnabled: false,
    htmlBlock: '',
    htmlBlockEnabled: false,
    sendDay: 1,
    frequencyWindowDays: 3,
    
    // Ads
    ads: [
      { imageUrl: '', adLink: '', position: 1 }
    ] as Ad[],
    
    // Schedule
    schedule: {
      enabled: false,
      minute: 0,
      hour: 9,
      day: 1,
      month: 1,
      year: new Date().getFullYear(),
      weekdays: [] as string[]
    }
  });
  const [createError, setCreateError] = useState('');
  const [sectionOrder, setSectionOrder] = useState(['latest', 'recommended', 'youMayAlsoLike', 'trending', 'featured', 'vault', 'textContent', 'htmlBlock', 'trendingVideos']); // Track section order

  // File Manager state
  const [fileManagerOpen, setFileManagerOpen] = useState(false);
  const [fileManagerTarget, setFileManagerTarget] = useState<{
    type: 'headerLogo' | 'articleImage' | 'vaultImage' | 'adImage';
    index?: number;
  } | null>(null);

  // Move to Folder state
  const [moveFolderDialog, setMoveFolderDialog] = useState(false);
  const [newsletterToMove, setNewsletterToMove] = useState<string | null>(null);
  const [selectedDestinationFolder, setSelectedDestinationFolder] = useState<string>('');
  const [availableFolders, setAvailableFolders] = useState<any[]>([]);

  useEffect(() => {
    if (filteredNewsletters) {
      setNewsletters(filteredNewsletters);
      setLoading(false);
    } else {
      fetchNewsletters();
    }
  }, [filteredNewsletters]);

  // Auto-dismiss success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchNewsletters = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      
      const response = await fetch(`${apiUrl}/prod/newsletter-management`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch newsletters');
      const data = await response.json();
      setNewsletters(data.newsletters || []);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch newsletters');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get enabled state from content order
  const getEnabledStateFromContentOrder = (contentOrder: any[], key: string): boolean => {
    if (!contentOrder || !Array.isArray(contentOrder)) {
      return false;
    }
    const item = contentOrder.find((item: any) => item.key === key);
    const checkValue = item?.checkValue;
    // Handle both boolean and string values
    return checkValue === true || checkValue === 'true';
  };
  
  // Helper function to convert string boolean to actual boolean
  const parseBoolean = (value: any): boolean => {
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'string') {
      return value.toLowerCase() === 'true';
    }
    return false;
  };

  // Function to populate form with existing newsletter data
  const populateNewsletterForm = (newsletterData: any) => {
    try {
      const loadedAudience = newsletterData.audienceVariation || newsletterData.newsletterType || '';
      const isCarAndDriverSyncAudience = isCarAndDriverSyncAudienceValue(loadedAudience);
      const isDailyOnlyReEngagementAudience = isDailyOnlyReEngagementAudienceValue(loadedAudience);
      const resolveEnabledState = (storedValue: any, contentOrderKey: string): boolean => (
        storedValue !== undefined
          ? parseBoolean(storedValue)
          : getEnabledStateFromContentOrder(newsletterData.newsletterDataContentOrder, contentOrderKey)
      );
      // Use the individual fields directly since we're no longer storing content field
      setNewNewsletter({
        audience: loadedAudience,
        newsletterName: isCarAndDriverSyncAudience ? CAR_AND_DRIVER_SYNC_NAME : (newsletterData.newsletterName || newsletterData.title || ''),
        subject: isCarAndDriverSyncAudience ? CAR_AND_DRIVER_SYNC_NAME : (newsletterData.newsletterTitle || ''),
        enableLlmSubjectLine: isCarAndDriverSyncAudience ? false : parseBoolean(newsletterData.enableLlmSubjectLine),
        previewText: isCarAndDriverSyncAudience ? CAR_AND_DRIVER_SYNC_NAME : (newsletterData.newsletterPreviewText || ''),
        status: newsletterData.status || 'draft',
        headerLogo: isCarAndDriverSyncAudience ? '' : (newsletterData.headerLogo || ''),
        headerText: isCarAndDriverSyncAudience ? '' : (newsletterData.headerText || ''),
        footerText: isCarAndDriverSyncAudience ? '' : (newsletterData.footerText || DEFAULT_FOOTER_TEXT),
        footerLinks: isCarAndDriverSyncAudience ? [] : (newsletterData.footerLinks || DEFAULT_FOOTER_LINKS),
        articles: isCarAndDriverSyncAudience ? [] : (newsletterData.newsletterDataTitleUrl?.map((item: any) => ({
          title: item.title || '',
          url: item.url || '',
          imageUrl: item.imageUrl || '',
          selected: item.selected !== false  // Default to true unless explicitly false
        })) || [{ title: '', url: '', imageUrl: '', selected: true }]),
        featuredArticlesEnabled: isCarAndDriverSyncAudience ? false : resolveEnabledState(newsletterData.featuredArticlesEnabled, 'manual'),
        vaultArticles: isCarAndDriverSyncAudience ? [] : (newsletterData.newsletterFromTheVaultTitleUrl?.map((item: any) => ({
          title: item.title || '',
          url: item.url || '',
          imageUrl: item.imageUrl || '',
          selected: item.selected || false
        })) || [{ title: '', url: '', imageUrl: '', selected: false }]),
        vaultArticlesEnabled: isCarAndDriverSyncAudience ? false : resolveEnabledState(newsletterData.vaultArticlesEnabled, 'from-the-vault'),
        latestArticles: isCarAndDriverSyncAudience ? [] : (newsletterData.latestArticles || []),
        latestArticlesEnabled: isCarAndDriverSyncAudience ? false : resolveEnabledState(newsletterData.latestArticlesEnabled, 'latest'),
        recommendedArticles: isCarAndDriverSyncAudience ? [] : (newsletterData.recommendedArticles || []),
        recommendedArticlesEnabled: isCarAndDriverSyncAudience ? true : resolveEnabledState(newsletterData.recommendedArticlesEnabled, 'recommended_for_you'),
        youMayAlsoLikeArticles: isCarAndDriverSyncAudience ? [] : (newsletterData.youMayAlsoLikeArticles || []),
        youMayAlsoLikeArticlesEnabled: isCarAndDriverSyncAudience ? false : resolveEnabledState(newsletterData.youMayAlsoLikeArticlesEnabled, 'you_may_also_like'),
        trendingArticles: isCarAndDriverSyncAudience ? [] : (newsletterData.trendingArticles || []),
        trendingArticlesEnabled: isCarAndDriverSyncAudience ? false : resolveEnabledState(newsletterData.trendingArticlesEnabled, 'trending'),
        trendingVideos: isCarAndDriverSyncAudience ? [] : (newsletterData.trendingVideos || []),
        trendingVideosEnabled: isCarAndDriverSyncAudience ? false : resolveEnabledState(newsletterData.trendingVideosEnabled, 'video'),
        textContent: isCarAndDriverSyncAudience ? '' : (newsletterData.textContent ? decodeURIComponent(escape(atob(newsletterData.textContent))) : ''),
        textContentEnabled: isCarAndDriverSyncAudience ? false : resolveEnabledState(newsletterData.textContentEnabled, 'textContent'),
        htmlBlock: isCarAndDriverSyncAudience ? '' : (newsletterData.htmlBlock ? decodeURIComponent(escape(atob(newsletterData.htmlBlock))) : ''),
        htmlBlockEnabled: isCarAndDriverSyncAudience ? false : resolveEnabledState(newsletterData.htmlBlockEnabled, 'htmlBlock'),
        sendDay: Number(newsletterData.sendDay || 1),
        frequencyWindowDays: Number(newsletterData.frequencyWindowDays || 3),
        ads: isCarAndDriverSyncAudience ? [] : (newsletterData.newsletterAds?.map((ad: any) => ({
          imageUrl: ad.adsImageURL || ad.adsImage || ad.imageUrl || '',
          adLink: ad.adsUrl || ad.adLink || '',
          position: ad.adsposition || ad.position || 1
        })) || [{ imageUrl: '', adLink: '', position: 1 }]),
        schedule: {
          ...(newsletterData.schedule || {
            enabled: false,
            minute: 0,
            hour: 9,
            day: 1,
            month: 1,
            year: new Date().getFullYear(),
            weekdays: []
          }),
          enabled: isDailyOnlyReEngagementAudience ? false : Boolean(newsletterData.schedule?.enabled)
        }
      });
      
      // Load section order from database
      if (isCarAndDriverSyncAudience) {
        setSectionOrder([]);
        setActiveTab(4);
      } else if (isRateYourCarAudienceValue(loadedAudience)) {
        setSectionOrder(['textContent']);
      } else if (isReEngagementAudienceValue(loadedAudience)) {
        setSectionOrder(isConfigurableReviewReEngagementAudienceValue(loadedAudience) ? ['htmlBlock'] : []);
      } else if (newsletterData.newsletterDataContentOrder && Array.isArray(newsletterData.newsletterDataContentOrder)) {
        // Extract the section types from the content order objects
        const loadedSectionOrder = newsletterData.newsletterDataContentOrder.map((item: any) => {
          // Map the key back to section type
          switch (item.key) {
            case 'latest': return 'latest';
            case 'recommended_for_you': return 'recommended';
            case 'you_may_also_like': return 'youMayAlsoLike';
            case 'trending': return 'trending';
            case 'manual': return 'featured';
            case 'from-the-vault': return 'vault';
            case 'textContent': return 'textContent';
            case 'htmlBlock': return 'htmlBlock';
            case 'video': return 'trendingVideos';
            default: return item.key;
          }
        });
        setSectionOrder(loadedSectionOrder);
      } else {
        // Use default order if not present
        setSectionOrder(['latest', 'recommended', 'youMayAlsoLike', 'trending', 'featured', 'vault', 'textContent', 'htmlBlock', 'trendingVideos']);
      }
    } catch (error: any) {
      setCreateError('Failed to load newsletter data. Please try again.');
    }
  };

  const handleEdit = async (id: string) => {
    try {
      // Find the newsletter in the existing newsletters array
      const newsletterToEdit = newsletters.find(newsletter => newsletter.id === id);
      
      if (!newsletterToEdit) {
        setError('Newsletter not found');
        return;
      }
      
      // Populate the form with existing data
      populateNewsletterForm(newsletterToEdit);
      
      // Set edit mode
      setIsEditing(true);
      setEditingNewsletterId(id);
      setCreateDialog(true);
      setCreateError('');
      setActiveTab(isCarAndDriverSyncAudienceValue(newsletterToEdit.audienceVariation || newsletterToEdit.newsletterType || '') ? 4 : 0);
      
    } catch (err: any) {
      setError(err.message || 'Failed to load newsletter for editing');
    }
  };

  const handleCopy = async (id: string) => {
    try {
      // Find the newsletter in the existing newsletters array
      const newsletterToCopy = newsletters.find(newsletter => newsletter.id === id);
      
      if (!newsletterToCopy) {
        setError('Newsletter not found');
        return;
      }
      
      // Populate the form with existing data but modify the name
      populateNewsletterForm(newsletterToCopy);
      
      // Modify the newsletter name to indicate it's a copy
      const isCarAndDriverSyncCopy = isCarAndDriverSyncAudienceValue(newsletterToCopy.audienceVariation || newsletterToCopy.newsletterType || '');
      setNewNewsletter(prev => ({
        ...prev,
        newsletterName: isCarAndDriverSyncCopy ? CAR_AND_DRIVER_SYNC_NAME : `${prev.newsletterName} (Copy)`,
        subject: isCarAndDriverSyncCopy ? CAR_AND_DRIVER_SYNC_NAME : prev.subject,
        previewText: isCarAndDriverSyncCopy ? CAR_AND_DRIVER_SYNC_NAME : prev.previewText,
        status: 'draft' // Always set to draft when copying
      }));
      
      // Set to create mode (not edit mode)
      setIsEditing(false);
      setEditingNewsletterId(null);
      setCreateDialog(true);
      setCreateError('');
      setActiveTab(isCarAndDriverSyncAudienceValue(newsletterToCopy.audienceVariation || newsletterToCopy.newsletterType || '') ? 4 : 0);
      
    } catch (err: any) {
      setError(err.message || 'Failed to copy newsletter');
    }
  };

  const handleDelete = async (id: string) => {
    // Show confirmation dialog
    const confirmed = window.confirm('Are you sure you want to delete this newsletter? This action cannot be undone.');
    
    if (!confirmed) {
      return;
    }

    try {
      const token = getAuthToken();
      
      const response = await fetch(`${apiUrl}/prod/newsletter-management/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete newsletter');
      }

      setSuccessMessage('Newsletter deleted successfully');
      
      // Refresh the newsletter list
      await fetchNewsletters();
      
    } catch (err: any) {
      setError(err.message || 'Failed to delete newsletter');
    }
  };

  const handleManualAutomatedCampaignRun = async (newsletter: Newsletter) => {
    const newsletterType = getNewsletterTypeFromAudience(
      newsletter.audienceVariation || newsletter.newsletterType || ''
    );
    if (
      newsletter.status !== 'published' ||
      (newsletterType !== 'automate_rating_reengagement_email' &&
        newsletterType !== 'automate_review_reengagement_email')
    ) {
      return;
    }

    const confirmed = window.confirm(
      `Start a manual ${newsletterType === 'automate_rating_reengagement_email' ? 'rating' : 'review'} re-engagement run now? It will send to all currently eligible recipients.`
    );
    if (!confirmed) return;

    try {
      setTriggeringCampaignId(newsletter.id);
      const token = getAuthToken();
      const response = await fetch(`${apiUrl}/prod/trigger-automated-reengagement`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ newsletterType, entryId: newsletter.id })
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.message || result.error || 'Failed to start automated campaign');
      }

      setError('');
      setSuccessMessage(`Manual ${newsletterType === 'automate_rating_reengagement_email' ? 'rating' : 'review'} campaign run started.`);
    } catch (err: any) {
      setError(err.message || 'Failed to start automated campaign');
    } finally {
      setTriggeringCampaignId(null);
    }
  };


  const handleAdd = () => {
    setIsEditing(false);
    setEditingNewsletterId(null);
    setCreateDialog(true);
    setCreateError('');
    setActiveTab(0);
    setNewNewsletter({
      audience: '',
      newsletterName: '',
      subject: '',
      enableLlmSubjectLine: true,
      previewText: '',
      status: 'draft',
      headerLogo: '',
      headerText: '',
      footerText: DEFAULT_FOOTER_TEXT,
      footerLinks: DEFAULT_FOOTER_LINKS,
      articles: [{ title: '', url: '', imageUrl: '', selected: true }],
      featuredArticlesEnabled: true,
      vaultArticles: [
        { title: '', url: '', imageUrl: '', selected: false }
      ],
      vaultArticlesEnabled: false,
      latestArticles: [],
      latestArticlesEnabled: false,
      recommendedArticles: [],
      recommendedArticlesEnabled: false,
      youMayAlsoLikeArticles: [],
      youMayAlsoLikeArticlesEnabled: false,
      trendingArticles: [],
      trendingArticlesEnabled: false,
      trendingVideos: [],
      trendingVideosEnabled: false,
      textContent: '',
      textContentEnabled: false,
      htmlBlock: '',
      htmlBlockEnabled: false,
      sendDay: 1,
      frequencyWindowDays: 3,
      ads: [{ imageUrl: '', adLink: '', position: 1 }],
      schedule: {
        enabled: false,
        minute: 0,
        hour: 9,
        day: 1,
        month: 1,
        year: new Date().getFullYear(),
        weekdays: []
      }
    });
    
    // Reset section order to default
    setSectionOrder(['latest', 'recommended', 'youMayAlsoLike', 'trending', 'featured', 'vault', 'textContent', 'htmlBlock', 'trendingVideos']);
  };

  const handleCreateNewsletter = async (status: string = 'draft') => {
    // Capture the status parameter at the very beginning to avoid any closure issues
    const newsletterStatus = status;
    
    // Validate form
    if (!newNewsletter.audience || !newNewsletter.newsletterName || !newNewsletter.subject.trim()) {
      setCreateError('Audience, newsletter name, and subject are required');
      return;
    }

    const isRateYourCarAudience = isRateYourCarAudienceValue(newNewsletter.audience);
    const isReEngagementAudience = isReEngagementAudienceValue(newNewsletter.audience);
    const isDailyOnlyReEngagementAudience = isDailyOnlyReEngagementAudienceValue(newNewsletter.audience);
    const isCarAndDriverSyncAudience = isCarAndDriverSyncAudienceValue(newNewsletter.audience);
    if (isRateYourCarAudience && !newNewsletter.sendDay) {
      setCreateError('Rate Your Car send day is required');
      return;
    }

    try {
      const token = getAuthToken();
      const isConfigurableReviewReEngagementAudience = isConfigurableReviewReEngagementAudienceValue(newNewsletter.audience);
      const effectiveSectionOrder = isCarAndDriverSyncAudience || (isReEngagementAudience && !isConfigurableReviewReEngagementAudience)
        ? []
        : (isRateYourCarAudience ? ['textContent'] : sectionOrder);
      const effectiveAds = isCarAndDriverSyncAudience ? [] : newNewsletter.ads;
      const effectiveArticles = isCarAndDriverSyncAudience ? [] : newNewsletter.articles;
      const effectiveVaultArticles = isCarAndDriverSyncAudience ? [] : newNewsletter.vaultArticles;
      const effectiveTextContent = isCarAndDriverSyncAudience ? '' : newNewsletter.textContent;
      const effectiveHtmlBlock = isConfigurableReviewReEngagementAudience ? newNewsletter.htmlBlock : '';
      
      // Structure data according to the schema
      const newsletterData = {
        title: newNewsletter.newsletterName,
        status: newsletterStatus,
        author: `${currentUser?.firstName} ${currentUser?.lastName}`,
        createdAt: isEditing ? undefined : new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        folderId: selectedFolder || undefined,
        
        // Newsletter specific fields
        newsletterName: newNewsletter.newsletterName,
        newsletterType: getNewsletterTypeFromAudience(newNewsletter.audience),
        newsletterTitle: newNewsletter.subject.trim(),
        newsletterPreviewText: newNewsletter.previewText,
        headerLogo: isCarAndDriverSyncAudience ? '' : newNewsletter.headerLogo,
        audienceVariation: newNewsletter.audience,
        headerText: isCarAndDriverSyncAudience ? '' : newNewsletter.headerText,
        sendDay: isRateYourCarAudience ? Number(newNewsletter.sendDay) : undefined,
        frequencyWindowDays: isReEngagementAudience ? Number(newNewsletter.frequencyWindowDays || 3) : undefined,
        
        // Header navigation (if needed)
        headerNav: {
          headerNavText: '',
          headerNavURL: ''
        },
        
        // Newsletter ads
        newsletterAds: effectiveAds.map(ad => ({
          adsImageType: 'image',
          adsImageURL: ad.imageUrl,
          adsImage: ad.imageUrl,
          adsUrl: ad.adLink,
          adsposition: ad.position
        })),
        
        // Footer
        footerText: isCarAndDriverSyncAudience ? '' : newNewsletter.footerText,
        footerLinks: isCarAndDriverSyncAudience ? [] : newNewsletter.footerLinks,
        
        // Content order - use current sectionOrder state
        newsletterDataContentOrder: effectiveSectionOrder.map(sectionType => {
          const getCheckValue = () => {
            switch (sectionType) {
              case 'latest': return newNewsletter.latestArticlesEnabled ? "true" : "false";
              case 'recommended': return newNewsletter.recommendedArticlesEnabled ? "true" : "false";
              case 'youMayAlsoLike': return newNewsletter.youMayAlsoLikeArticlesEnabled ? "true" : "false";
              case 'trending': return newNewsletter.trendingArticlesEnabled ? "true" : "false";
              case 'featured': return newNewsletter.featuredArticlesEnabled ? "true" : "false";
              case 'vault': return newNewsletter.vaultArticlesEnabled ? "true" : "false";
              case 'textContent': return newNewsletter.textContentEnabled ? "true" : "false";
              case 'htmlBlock': return newNewsletter.htmlBlockEnabled ? "true" : "false";
              case 'trendingVideos': return newNewsletter.trendingVideosEnabled ? "true" : "false";
              default: return "false";
            }
          };
          
          const getKey = () => {
            switch (sectionType) {
              case 'latest': return "latest";
              case 'recommended': return "recommended_for_you";
              case 'youMayAlsoLike': return "you_may_also_like";
              case 'trending': return "trending";
              case 'featured': return "manual";
              case 'vault': return "from-the-vault";
              case 'textContent': return "textContent";
              case 'htmlBlock': return "htmlBlock";
              case 'trendingVideos': return "video";
              default: return sectionType;
            }
          };
          
          const getValue = () => {
            switch (sectionType) {
              case 'latest': return "Latest Articles";
              case 'recommended': return "Recommended For You";
              case 'youMayAlsoLike': return "You May Also Like";
              case 'trending': return "Trending Articles";
              case 'featured': return "Manual(Featured Stories)";
              case 'vault': return "Manual(From the Vault)";
              case 'textContent': return "Text Content";
              case 'htmlBlock': return "HTML Block";
              case 'trendingVideos': return "Trending Videos";
              default: return sectionType;
            }
          };
          
          return {
            checkValue: getCheckValue(),
            key: getKey(),
            value: getValue()
          };
        }),
        
        // Text Content (Base64 encoded with UTF-8 support)
        textContent: effectiveTextContent ? btoa(unescape(encodeURIComponent(effectiveTextContent))) : '',
        htmlBlock: effectiveHtmlBlock ? btoa(unescape(encodeURIComponent(effectiveHtmlBlock))) : '',
        htmlBlockEnabled: isConfigurableReviewReEngagementAudience ? newNewsletter.htmlBlockEnabled : false,
        
        // Newsletter data title URL - save all articles with content
        newsletterDataTitleUrl: effectiveArticles
          .filter(article => article.title || article.url)
          .map(article => ({
            title: article.title,
            url: article.url,
            imageUrl: article.imageUrl,
            selected: article.selected
          })),
        
        // From the vault title URL - save all articles with content
        newsletterFromTheVaultTitleUrl: effectiveVaultArticles
          .filter(article => article.title || article.url)
          .map(article => ({
            title: article.title,
            url: article.url,
            imageUrl: article.imageUrl,
            selected: article.selected
          })),
        
        // Events manual data
        newsletterEventsManualData: effectiveAds.map(ad => ({
          image: ad.imageUrl,
          url: ad.adLink
        })),
        
        // AI subject line toggle
        enableLlmSubjectLine: isCarAndDriverSyncAudience ? false : newNewsletter.enableLlmSubjectLine,

        // Section enable flags
        latestArticlesEnabled: isCarAndDriverSyncAudience ? false : newNewsletter.latestArticlesEnabled,
        recommendedArticlesEnabled: isCarAndDriverSyncAudience ? true : newNewsletter.recommendedArticlesEnabled,
        youMayAlsoLikeArticlesEnabled: isCarAndDriverSyncAudience ? false : newNewsletter.youMayAlsoLikeArticlesEnabled,
        trendingArticlesEnabled: isCarAndDriverSyncAudience ? false : newNewsletter.trendingArticlesEnabled,
        featuredArticlesEnabled: isCarAndDriverSyncAudience ? false : newNewsletter.featuredArticlesEnabled,
        vaultArticlesEnabled: isCarAndDriverSyncAudience ? false : newNewsletter.vaultArticlesEnabled,
        trendingVideosEnabled: isCarAndDriverSyncAudience ? false : newNewsletter.trendingVideosEnabled,
        
        // Schedule
        schedule: {
          ...newNewsletter.schedule,
          enabled: isDailyOnlyReEngagementAudience ? false : newNewsletter.schedule.enabled
        }
      };

      const url = isEditing 
        ? `${apiUrl}/prod/newsletter-management/${editingNewsletterId}`
        : `${apiUrl}/prod/newsletter-management`;
      
      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newsletterData)
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || `Failed to ${isEditing ? 'update' : 'create'} newsletter`);
      }

      const result = await response.json();
      
      // Schedule the newsletter
      try {
        const schedulePayload = {
          values: {
            min: newNewsletter.schedule.minute.toString(),
            hours: newNewsletter.schedule.hour.toString(),
            day: newNewsletter.schedule.day.toString(),
            month: newNewsletter.schedule.month.toString(),
            weekdays: newNewsletter.schedule.weekdays.length > 0 ? newNewsletter.schedule.weekdays.join(',') : '?',
            year: newNewsletter.schedule.year.toString(),
            newsletterType: getNewsletterTypeFromAudience(newNewsletter.audience),
            sendDay: isRateYourCarAudience ? Number(newNewsletter.sendDay) : undefined,
            frequencyWindowDays: isReEngagementAudience ? Number(newNewsletter.frequencyWindowDays || 3) : undefined,
            scheduled: !isDailyOnlyReEngagementAudience && newNewsletter.schedule.enabled,
          },
          entryId: result.id || editingNewsletterId,
          status: newsletterStatus,
          apiDomain: apiUrl
        };

        const scheduleResponse = await fetch(`${apiUrl}/prod/schedule-newsletter`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            
          },
          body: JSON.stringify(schedulePayload)
        });

        if (!scheduleResponse.ok) {
          throw new Error(await scheduleResponse.text());
        }
      } catch (scheduleError: any) {
        // Don't fail the main operation if scheduling fails
        setCreateError(scheduleError.message || 'Failed to schedule newsletter');
      }
      
      setCreateError('');
      setSuccessMessage(`Newsletter ${isEditing ? 'updated' : 'created'} successfully`);

      if (newsletterStatus === 'draft') {
        keepDialogOpenRef.current = true;
        setKeepDialogOpen(true);
      } else {
        keepDialogOpenRef.current = false;
        setKeepDialogOpen(false);
      }
      
      if (!isEditing && result.id) {
        setIsEditing(true);
        setEditingNewsletterId(result.id);
      }

      if (onNewsletterCreated) {
        onNewsletterCreated();
      }

      if (newsletterStatus === 'published') {
        closeCreateDialog(true);
      }
      
    } catch (err: any) {
      setCreateError(err.message || `Failed to ${isEditing ? 'update' : 'create'} newsletter`);
    }
  };

  const closeCreateDialog = (forceClose: boolean = false) => {
    const shouldKeepOpen = keepDialogOpenRef.current || keepDialogOpen;
    if (forceClose) {
      setCreateDialog(false);
      setCreateError('');
      setIsEditing(false);
      setEditingNewsletterId(null);
      setKeepDialogOpen(false);
      keepDialogOpenRef.current = false;
    } else if (!shouldKeepOpen) {
      setCreateDialog(false);
      setCreateError('');
      setIsEditing(false);
      setEditingNewsletterId(null);
      setKeepDialogOpen(false);
      keepDialogOpenRef.current = false;
    }
  };

  const addArticle = () => {
    setNewNewsletter({
      ...newNewsletter,
      articles: [...newNewsletter.articles, { title: '', url: '', imageUrl: '', selected: true }]
    });
  };

  const removeArticle = (index: number) => {
    const updatedArticles = newNewsletter.articles.filter((_, i) => i !== index);
    setNewNewsletter({ ...newNewsletter, articles: updatedArticles });
  };

  const updateArticle = (index: number, field: keyof Article, value: string) => {
    const updatedArticles = [...newNewsletter.articles];
    updatedArticles[index] = { ...updatedArticles[index], [field]: value };
    setNewNewsletter({ ...newNewsletter, articles: updatedArticles });
  };

  const moveUpArticle = (index: number) => {
    if (index > 0) {
      const updatedArticles = [...newNewsletter.articles];
      [updatedArticles[index], updatedArticles[index - 1]] = [updatedArticles[index - 1], updatedArticles[index]];
      setNewNewsletter({ ...newNewsletter, articles: updatedArticles });
    }
  };

  const moveDownArticle = (index: number) => {
    if (index < newNewsletter.articles.length - 1) {
      const updatedArticles = [...newNewsletter.articles];
      [updatedArticles[index], updatedArticles[index + 1]] = [updatedArticles[index + 1], updatedArticles[index]];
      setNewNewsletter({ ...newNewsletter, articles: updatedArticles });
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const toggleVaultArticle = (index: number) => {
    const updatedVaultArticles = [...newNewsletter.vaultArticles];
    updatedVaultArticles[index] = { ...updatedVaultArticles[index], selected: !updatedVaultArticles[index].selected };
    setNewNewsletter({ ...newNewsletter, vaultArticles: updatedVaultArticles });
  };

  const moveUpVaultArticle = (index: number) => {
    if (index > 0) {
      const updatedVaultArticles = [...newNewsletter.vaultArticles];
      [updatedVaultArticles[index], updatedVaultArticles[index - 1]] = [updatedVaultArticles[index - 1], updatedVaultArticles[index]];
      setNewNewsletter({ ...newNewsletter, vaultArticles: updatedVaultArticles });
    }
  };

  const moveDownVaultArticle = (index: number) => {
    if (index < newNewsletter.vaultArticles.length - 1) {
      const updatedVaultArticles = [...newNewsletter.vaultArticles];
      [updatedVaultArticles[index], updatedVaultArticles[index + 1]] = [updatedVaultArticles[index + 1], updatedVaultArticles[index]];
      setNewNewsletter({ ...newNewsletter, vaultArticles: updatedVaultArticles });
    }
  };

  const addVaultArticle = () => {
    const isHotRodAudience = newNewsletter.audience === 'Newsletter_HotRod' || newNewsletter.audience === 'test_hotrod_newsletter';
    setNewNewsletter({
      ...newNewsletter,
      vaultArticles: [...newNewsletter.vaultArticles, { title: '', url: '', imageUrl: '', selected: isHotRodAudience }]
    });
  };

  const removeVaultArticle = (index: number) => {
    const updatedVaultArticles = newNewsletter.vaultArticles.filter((_, i) => i !== index);
    setNewNewsletter({ ...newNewsletter, vaultArticles: updatedVaultArticles });
  };

  const updateVaultArticle = (index: number, field: keyof VaultArticle, value: string) => {
    const updatedVaultArticles = [...newNewsletter.vaultArticles];
    updatedVaultArticles[index] = { ...updatedVaultArticles[index], [field]: value };
    setNewNewsletter({ ...newNewsletter, vaultArticles: updatedVaultArticles });
  };

  const addAd = () => {
    setNewNewsletter({
      ...newNewsletter,
      ads: [...newNewsletter.ads, { imageUrl: '', adLink: '', position: 1 }]
    });
  };

  const removeAd = (index: number) => {
    const updatedAds = newNewsletter.ads.filter((_, i) => i !== index);
    setNewNewsletter({ ...newNewsletter, ads: updatedAds });
  };

  const updateAd = (index: number, field: keyof Ad, value: string | number) => {
    const updatedAds = [...newNewsletter.ads];
    updatedAds[index] = { ...updatedAds[index], [field]: value };
    setNewNewsletter({ ...newNewsletter, ads: updatedAds });
  };

  const moveUpAd = (index: number) => {
    if (index > 0) {
      const updatedAds = [...newNewsletter.ads];
      [updatedAds[index], updatedAds[index - 1]] = [updatedAds[index - 1], updatedAds[index]];
      setNewNewsletter({ ...newNewsletter, ads: updatedAds });
    }
  };

  const moveDownAd = (index: number) => {
    if (index < newNewsletter.ads.length - 1) {
      const updatedAds = [...newNewsletter.ads];
      [updatedAds[index], updatedAds[index + 1]] = [updatedAds[index + 1], updatedAds[index]];
      setNewNewsletter({ ...newNewsletter, ads: updatedAds });
    }
  };

  const updateFooterLink = (index: number, field: keyof FooterLink, value: string) => {
    const updatedLinks = [...newNewsletter.footerLinks];
    updatedLinks[index] = { ...updatedLinks[index], [field]: value };
    setNewNewsletter({ ...newNewsletter, footerLinks: updatedLinks });
  };


  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Handle audience selection and update footer text, links, header logo, and header text accordingly
  const handleAudienceChange = (audience: string) => {
    const isHotRodAudience = audience === 'Newsletter_HotRod' || audience === 'test_hotrod_newsletter';
    const isMotorTrendAudience = audience === 'Newsletter_MotorTrend' || audience === 'test_motortrend_newsletter';
    const isEventsHotRodAudience =
      audience === 'Newsletter_Events_HotRod' || audience === 'test_events_hotrod_newsletter';
    const isEventsMotorTrendAudience =
      audience === 'Newsletter_Events_MotorTrend' || audience === 'test_events_motortrend_newsletter';
    const isRateYourCarAudience = isRateYourCarAudienceValue(audience);
    const isReEngagementAudience = isReEngagementAudienceValue(audience);
    const isDailyOnlyReEngagementAudience = isDailyOnlyReEngagementAudienceValue(audience);
    const isManuallySchedulableReEngagementAudience = isManuallySchedulableReEngagementAudienceValue(audience);
    const isCarAndDriverSyncAudience = isCarAndDriverSyncAudienceValue(audience);

    let footerText = DEFAULT_FOOTER_TEXT;
    let footerLinks = DEFAULT_FOOTER_LINKS;
    let headerLogo = '';
    let headerText = '';
    let newSectionOrder: string[] = [];

    if (isCarAndDriverSyncAudience) {
      footerText = '';
      footerLinks = [];
      headerLogo = '';
      headerText = '';
      newSectionOrder = [];
    } else if (isRateYourCarAudience) {
      footerText = MOTORTREND_FOOTER_TEXT;
      footerLinks = MOTORTREND_FOOTER_LINKS;
      headerLogo = MOTORTREND_HEADER_LOGO;
      headerText = MOTORTREND_HEADER_TEXT;
      newSectionOrder = ['textContent'];
    } else if (isReEngagementAudience) {
      footerText = MOTORTREND_FOOTER_TEXT;
      footerLinks = MOTORTREND_FOOTER_LINKS;
      headerLogo = MOTORTREND_HEADER_LOGO;
      headerText = MOTORTREND_HEADER_TEXT;
      newSectionOrder = isConfigurableReviewReEngagementAudienceValue(audience) ? ['htmlBlock'] : [];
    } else if (isHotRodAudience) {
      footerText = HOT_ROD_FOOTER_TEXT;
      footerLinks = HOT_ROD_FOOTER_LINKS;
      headerLogo = HOT_ROD_HEADER_LOGO;
      headerText = HOT_ROD_HEADER_TEXT;
      // Hot Rod: show all sections
      newSectionOrder = ['latest', 'recommended', 'youMayAlsoLike', 'trending', 'featured', 'vault', 'textContent', 'trendingVideos'];
    } else if (isMotorTrendAudience) {
      footerText = MOTORTREND_FOOTER_TEXT;
      footerLinks = MOTORTREND_FOOTER_LINKS;
      headerLogo = MOTORTREND_HEADER_LOGO;
      headerText = MOTORTREND_HEADER_TEXT;
      // MotorTrend: show all sections except vault
      newSectionOrder = ['latest', 'recommended', 'youMayAlsoLike', 'trending', 'featured', 'textContent', 'trendingVideos'];
    } else if (isEventsHotRodAudience) {
      // Events newsletter branded as Hot Rod
      footerText = HOT_ROD_FOOTER_TEXT;
      footerLinks = HOT_ROD_FOOTER_LINKS;
      headerLogo = HOT_ROD_HEADER_LOGO;
      headerText = HOT_ROD_HEADER_TEXT;
      newSectionOrder = ['featured', 'textContent'];
    } else if (isEventsMotorTrendAudience) {
      // Events newsletter branded as MotorTrend
      footerText = MOTORTREND_FOOTER_TEXT;
      footerLinks = MOTORTREND_FOOTER_LINKS;
      headerLogo = MOTORTREND_HEADER_LOGO;
      headerText = MOTORTREND_HEADER_TEXT;
      newSectionOrder = ['featured', 'textContent'];
    } else {
      // Default: show all sections
      newSectionOrder = ['latest', 'recommended', 'youMayAlsoLike', 'trending', 'featured', 'vault', 'textContent', 'trendingVideos'];
    }
    
    // For Hot Rod newsletters, auto-select vault articles
    const updatedVaultArticles = isHotRodAudience 
      ? newNewsletter.vaultArticles.map(article => ({ ...article, selected: true }))
      : newNewsletter.vaultArticles;
    
    setNewNewsletter({ 
      ...newNewsletter, 
      audience: audience,
      footerText: footerText,
      footerLinks: footerLinks,
      headerLogo: headerLogo,
      headerText: headerText,
      newsletterName: isCarAndDriverSyncAudience ? CAR_AND_DRIVER_SYNC_NAME : newNewsletter.newsletterName,
      subject: isCarAndDriverSyncAudience ? CAR_AND_DRIVER_SYNC_NAME : newNewsletter.subject,
      previewText: isCarAndDriverSyncAudience ? CAR_AND_DRIVER_SYNC_NAME : newNewsletter.previewText,
      enableLlmSubjectLine: isCarAndDriverSyncAudience ? false : newNewsletter.enableLlmSubjectLine,
      articles: isCarAndDriverSyncAudience ? [] : newNewsletter.articles,
      vaultArticles: isCarAndDriverSyncAudience ? [] : updatedVaultArticles,
      latestArticles: isCarAndDriverSyncAudience ? [] : newNewsletter.latestArticles,
      recommendedArticles: isCarAndDriverSyncAudience ? [] : newNewsletter.recommendedArticles,
      youMayAlsoLikeArticles: isCarAndDriverSyncAudience ? [] : newNewsletter.youMayAlsoLikeArticles,
      trendingArticles: isCarAndDriverSyncAudience ? [] : newNewsletter.trendingArticles,
      trendingVideos: isCarAndDriverSyncAudience ? [] : newNewsletter.trendingVideos,
      textContent: isCarAndDriverSyncAudience ? '' : newNewsletter.textContent,
      htmlBlock: isCarAndDriverSyncAudience ? '' : newNewsletter.htmlBlock,
      ads: isCarAndDriverSyncAudience ? [] : newNewsletter.ads,
      latestArticlesEnabled: isCarAndDriverSyncAudience || isRateYourCarAudience || isReEngagementAudience ? false : newNewsletter.latestArticlesEnabled,
      recommendedArticlesEnabled: isCarAndDriverSyncAudience ? true : (isRateYourCarAudience || isReEngagementAudience ? false : newNewsletter.recommendedArticlesEnabled),
      youMayAlsoLikeArticlesEnabled: isCarAndDriverSyncAudience || isRateYourCarAudience || isReEngagementAudience ? false : newNewsletter.youMayAlsoLikeArticlesEnabled,
      trendingArticlesEnabled: isCarAndDriverSyncAudience || isRateYourCarAudience || isReEngagementAudience ? false : newNewsletter.trendingArticlesEnabled,
      featuredArticlesEnabled: isCarAndDriverSyncAudience || isRateYourCarAudience || isReEngagementAudience ? false : newNewsletter.featuredArticlesEnabled,
      vaultArticlesEnabled: isCarAndDriverSyncAudience || isRateYourCarAudience || isReEngagementAudience ? false : newNewsletter.vaultArticlesEnabled,
      trendingVideosEnabled: isCarAndDriverSyncAudience || isRateYourCarAudience || isReEngagementAudience ? false : newNewsletter.trendingVideosEnabled,
      textContentEnabled: isCarAndDriverSyncAudience || isReEngagementAudience ? false : (isRateYourCarAudience ? true : newNewsletter.textContentEnabled),
      htmlBlockEnabled: isConfigurableReviewReEngagementAudienceValue(audience),
      schedule: isDailyOnlyReEngagementAudience || isManuallySchedulableReEngagementAudience
        ? { ...newNewsletter.schedule, enabled: false }
        : newNewsletter.schedule,
      sendDay: isRateYourCarAudience ? (newNewsletter.sendDay || 1) : newNewsletter.sendDay
    });
    
    // Update section order based on audience type
    setSectionOrder(newSectionOrder);
    if (isCarAndDriverSyncAudience) {
      setActiveTab(4);
    }
  };

  const renderTabContent = (tabValue: number = activeTab) => {
    const isCarAndDriverSyncSelected = isCarAndDriverSyncAudienceValue(newNewsletter.audience);
    if (isCarAndDriverSyncSelected && tabValue !== 4) {
      return null;
    }

    switch (tabValue) {
      case 0: // Header
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, color: themeMode === 'dark' ? 'white' : '#b71c1c' }}>Header Configuration</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField
                    fullWidth
                    label="Header Logo URL"
                    value={newNewsletter.headerLogo}
                    onChange={(e) => setNewNewsletter({ ...newNewsletter, headerLogo: e.target.value })}
                    placeholder="https://example.com/logo.png"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: themeMode === 'light' ? '#ccc' : '#666',
                        },
                        '&:hover fieldset': {
                          borderColor: themeMode === 'light' ? '#999' : '#888',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#b71c1c',
                        },
                        '& .MuiInputBase-input': {
                          color: themeMode === 'light' ? '#333' : 'white',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: themeMode === 'light' ? '#333' : 'white',
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#b71c1c',
                      },
                    }}
                  />
                  <IconButton
                    onClick={() => handleOpenFileManager('headerLogo')}
                    sx={{ 
                      color: '#d32f2f', 
                      mt: 1,
                      '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.1)' }
                    }}
                    title="Select Image from File Manager"
                  >
                    <FolderOpenIcon />
                  </IconButton>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Header Text"
                  value={newNewsletter.headerText}
                  onChange={(e) => setNewNewsletter({ ...newNewsletter, headerText: e.target.value })}
                  placeholder="Enter header text"
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 1: { // Content
        const isRateYourCarAudience = isRateYourCarAudienceValue(newNewsletter.audience);
        if (isCarAndDriverSyncSelected) {
          return null;
        }
        return (
          <Box sx={{ p: 3 }}>
            {isRateYourCarAudience && (
              <Box sx={{ mb: 3 }}>
                <FormControl fullWidth sx={{ maxWidth: 320 }}>
                  <InputLabel>Rate Your Car Send Day</InputLabel>
                  <Select
                    value={newNewsletter.sendDay}
                    label="Rate Your Car Send Day"
                    onChange={(e) => setNewNewsletter({
                      ...newNewsletter,
                      sendDay: Number(e.target.value)
                    })}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: themeMode === 'light' ? '#ccc' : '#666',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: themeMode === 'light' ? '#999' : '#888',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#b71c1c',
                      },
                      '& .MuiSelect-select': {
                        color: themeMode === 'light' ? '#333' : 'white',
                      },
                    }}
                  >
                    {RATE_YOUR_CAR_SEND_DAY_OPTIONS.map((sendDay) => (
                      <MenuItem key={sendDay} value={sendDay}>Day {sendDay}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
            {sectionOrder.map((sectionType, sectionIndex) => {
              // Hide "From the Vault" section if audience doesn't contain "hotrod"
              if (sectionType === 'vault' && !newNewsletter.audience.toLowerCase().includes('hotrod')) {
                return null;
              }
              
              if (sectionType === 'featured') {
                const isEventsAudience = isEventsAudienceValue(newNewsletter.audience);
                
                // For Events newsletters, show Manual Event with Image and URL
                if (isEventsAudience) {
                  return (
                    <Box key="featured">
                      {sectionIndex > 0 && <Divider sx={{ my: 4 }} />}
                      {/* Manual Event Section */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Checkbox
                            checked={newNewsletter.featuredArticlesEnabled}
                            onChange={(e) => {
                              setNewNewsletter({ ...newNewsletter, featuredArticlesEnabled: e.target.checked });
                            }}
                            sx={{ color: themeMode === 'dark' ? 'white' : '#d32f2f', mr: 1 }}
                          />
                          <Typography variant="h6" sx={{ color: themeMode === 'dark' ? 'white' : '#b71c1c' }}>Manual Event</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <IconButton
                            size="small"
                            onClick={() => moveSectionUp('featured')}
                            disabled={sectionIndex === 0}
                            sx={{ color: '#d32f2f' }}
                            title="Move Section Up"
                          >
                            <KeyboardArrowUpIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => moveSectionDown('featured')}
                            disabled={sectionIndex === sectionOrder.length - 1}
                            sx={{ color: '#d32f2f' }}
                            title="Move Section Down"
                          >
                            <KeyboardArrowDownIcon />
                          </IconButton>
                          <IconButton
                            onClick={addAd}
                            sx={{ color: '#d32f2f', '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.1)' } }}
                            title="Add Manual Event"
                          >
                            <AddIcon />
                          </IconButton>
                        </Box>
                      </Box>
                      {newNewsletter.ads.map((ad, index) => (
                        <Box key={index} sx={{ mb: 3, p: 3, border: themeMode === 'light' ? '1px solid #ddd' : '1px solid #444', borderRadius: 2, bgcolor: themeMode === 'light' ? '#fafafa' : '#2a2a2a' }}>
                          <Grid container spacing={3}>
                            <Grid item xs={12}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: themeMode === 'dark' ? 'white' : 'inherit' }}>Manual Event {index + 1}</Typography>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                  {newNewsletter.ads.length > 1 && (
                                    <>
                                      <IconButton
                                        size="small"
                                        onClick={() => moveUpAd(index)}
                                        disabled={index === 0}
                                        sx={{ color: '#d32f2f' }}
                                        title="Move Up"
                                      >
                                        <KeyboardArrowUpIcon />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        onClick={() => moveDownAd(index)}
                                        disabled={index === newNewsletter.ads.length - 1}
                                        sx={{ color: '#d32f2f' }}
                                        title="Move Down"
                                      >
                                        <KeyboardArrowDownIcon />
                                      </IconButton>
                                    </>
                                  )}
                                  <IconButton 
                                    size="small"
                                    onClick={() => removeAd(index)}
                                    sx={{ color: '#d32f2f' }}
                                    title="Delete Manual Event"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Box>
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                <TextField
                                  fullWidth
                                  label="Image URL"
                                  value={ad.imageUrl}
                                  onChange={(e) => updateAd(index, 'imageUrl', e.target.value)}
                                  placeholder="https://example.com/image.jpg"
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      '& fieldset': {
                                        borderColor: themeMode === 'light' ? '#ccc' : '#666',
                                      },
                                      '&:hover fieldset': {
                                        borderColor: themeMode === 'light' ? '#999' : '#888',
                                      },
                                      '&.Mui-focused fieldset': {
                                        borderColor: '#b71c1c',
                                      },
                                      '& .MuiInputBase-input': {
                                        color: themeMode === 'light' ? '#333' : 'white',
                                      },
                                    },
                                    '& .MuiInputLabel-root': {
                                      color: themeMode === 'light' ? '#333' : 'white',
                                    },
                                    '& .MuiInputLabel-root.Mui-focused': {
                                      color: '#b71c1c',
                                    },
                                  }}
                                />
                                <IconButton
                                  onClick={() => handleOpenFileManager('adImage', index)}
                                  sx={{ 
                                    color: '#d32f2f', 
                                    mt: 1,
                                    '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.1)' }
                                  }}
                                  title="Select Image from File Manager"
                                >
                                  <FolderOpenIcon />
                                </IconButton>
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                label="URL"
                                value={ad.adLink}
                                onChange={(e) => updateAd(index, 'adLink', e.target.value)}
                                placeholder="https://example.com/event"
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                      borderColor: themeMode === 'light' ? '#ccc' : '#666',
                                    },
                                    '&:hover fieldset': {
                                      borderColor: themeMode === 'light' ? '#999' : '#888',
                                    },
                                    '&.Mui-focused fieldset': {
                                      borderColor: '#b71c1c',
                                    },
                                    '& .MuiInputBase-input': {
                                      color: themeMode === 'light' ? '#333' : 'white',
                                    },
                                  },
                                  '& .MuiInputLabel-root': {
                                    color: themeMode === 'light' ? '#333' : 'white',
                                  },
                                  '& .MuiInputLabel-root.Mui-focused': {
                                    color: '#b71c1c',
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      ))}
                    </Box>
                  );
                }
                
                // For non-Events newsletters, show Featured Articles
                return (
                  <Box key="featured">
                    {sectionIndex > 0 && <Divider sx={{ my: 4 }} />}
                    {/* Featured Articles Section */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Checkbox
                          checked={newNewsletter.featuredArticlesEnabled}
                          onChange={(e) => {
                            setNewNewsletter({ ...newNewsletter, featuredArticlesEnabled: e.target.checked });
                          }}
                          sx={{ color: themeMode === 'dark' ? 'white' : '#d32f2f', mr: 1 }}
                        />
                        <Typography variant="h6" sx={{ color: themeMode === 'dark' ? 'white' : '#b71c1c' }}>Featured Articles</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <IconButton
                          size="small"
                          onClick={() => moveSectionUp('featured')}
                          disabled={sectionIndex === 0}
                          sx={{ color: '#d32f2f' }}
                          title="Move Section Up"
                        >
                          <KeyboardArrowUpIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => moveSectionDown('featured')}
                          disabled={sectionIndex === sectionOrder.length - 1}
                          sx={{ color: '#d32f2f' }}
                          title="Move Section Down"
                        >
                          <KeyboardArrowDownIcon />
                        </IconButton>
                        <IconButton
                          onClick={addArticle}
                          sx={{ color: '#d32f2f', '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.1)' } }}
                          title="Add Article"
                        >
                          <AddIcon />
                        </IconButton>
                      </Box>
                    </Box>
                    {newNewsletter.articles.map((article, index) => (
                      <Box key={index} sx={{ mb: 3, p: 3, border: themeMode === 'light' ? '1px solid #ddd' : '1px solid #444', borderRadius: 2, bgcolor: themeMode === 'light' ? '#fafafa' : '#2a2a2a' }}>
                        <Grid container spacing={3}>
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: themeMode === 'dark' ? 'white' : 'inherit' }}>Featured Article {index + 1}</Typography>
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                {newNewsletter.articles.length > 1 && (
                                  <>
                                    <IconButton
                                      size="small"
                                      onClick={() => moveUpArticle(index)}
                                      disabled={index === 0}
                                      sx={{ color: '#d32f2f' }}
                                      title="Move Up"
                                    >
                                      <KeyboardArrowUpIcon />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => moveDownArticle(index)}
                                      disabled={index === newNewsletter.articles.length - 1}
                                      sx={{ color: '#d32f2f' }}
                                      title="Move Down"
                                    >
                                      <KeyboardArrowDownIcon />
                                    </IconButton>
                                    <IconButton 
                                      size="small"
                                      onClick={() => removeArticle(index)}
                                      sx={{ color: '#d32f2f' }}
                                      title="Delete Article"
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </>
                                )}
                              </Box>
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              label="Article Title"
                              value={article.title}
                              onChange={(e) => updateArticle(index, 'title', e.target.value)}
                              placeholder="Enter article title"
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              label="Article URL"
                              value={article.url}
                              onChange={(e) => updateArticle(index, 'url', e.target.value)}
                              placeholder="https://example.com/article"
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                              <TextField
                                fullWidth
                                label="Image URL"
                                value={article.imageUrl}
                                onChange={(e) => updateArticle(index, 'imageUrl', e.target.value)}
                                placeholder="https://example.com/image.jpg"
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                      borderColor: themeMode === 'light' ? '#ccc' : '#666',
                                    },
                                    '&:hover fieldset': {
                                      borderColor: themeMode === 'light' ? '#999' : '#888',
                                    },
                                    '&.Mui-focused fieldset': {
                                      borderColor: '#b71c1c',
                                    },
                                    '& .MuiInputBase-input': {
                                      color: themeMode === 'light' ? '#333' : 'white',
                                    },
                                  },
                                  '& .MuiInputLabel-root': {
                                    color: themeMode === 'light' ? '#333' : 'white',
                                  },
                                  '& .MuiInputLabel-root.Mui-focused': {
                                    color: '#b71c1c',
                                  },
                                }}
                              />
                              <IconButton
                                onClick={() => handleOpenFileManager('articleImage', index)}
                                sx={{ 
                                  color: '#d32f2f', 
                                  mt: 1,
                                  '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.1)' }
                                }}
                                title="Select Image from File Manager"
                              >
                                <FolderOpenIcon />
                              </IconButton>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    ))}
                  </Box>
                );
              } else if (sectionType === 'vault') {
                return (
                  <Box key="vault">
                    {sectionIndex > 0 && <Divider sx={{ my: 4 }} />}
                    {/* From the Vault Section */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Checkbox
                          checked={newNewsletter.vaultArticlesEnabled}
                          onChange={(e) => {
                            setNewNewsletter({ ...newNewsletter, vaultArticlesEnabled: e.target.checked });
                          }}
                          sx={{ color: themeMode === 'dark' ? 'white' : '#d32f2f', mr: 1 }}
                        />
                        <Typography variant="h6" sx={{ color: themeMode === 'dark' ? 'white' : '#b71c1c' }}>From the Vault</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <IconButton
                          size="small"
                          onClick={() => moveSectionUp('vault')}
                          disabled={sectionIndex === 0}
                          sx={{ color: '#d32f2f' }}
                          title="Move Section Up"
                        >
                          <KeyboardArrowUpIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => moveSectionDown('vault')}
                          disabled={sectionIndex === sectionOrder.length - 1}
                          sx={{ color: '#d32f2f' }}
                          title="Move Section Down"
                        >
                          <KeyboardArrowDownIcon />
                        </IconButton>
                        <IconButton
                          onClick={addVaultArticle}
                          sx={{ color: '#d32f2f', '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.1)' } }}
                          title="Add Vault Article"
                        >
                          <AddIcon />
                        </IconButton>
                      </Box>
                    </Box>
                    {newNewsletter.vaultArticles.map((article, index) => (
                      <Box key={index} sx={{ mb: 3, p: 3, border: themeMode === 'light' ? '1px solid #ddd' : '1px solid #444', borderRadius: 2, bgcolor: themeMode === 'light' ? '#fafafa' : '#2a2a2a' }}>
                        <Grid container spacing={3}>
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: themeMode === 'dark' ? 'white' : 'inherit' }}>Vault Article {index + 1}</Typography>
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                {newNewsletter.vaultArticles.length > 1 && (
                                  <>
                                    <IconButton
                                      size="small"
                                      onClick={() => moveUpVaultArticle(index)}
                                      disabled={index === 0}
                                      sx={{ color: '#d32f2f' }}
                                      title="Move Up"
                                    >
                                      <KeyboardArrowUpIcon />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => moveDownVaultArticle(index)}
                                      disabled={index === newNewsletter.vaultArticles.length - 1}
                                      sx={{ color: '#d32f2f' }}
                                      title="Move Down"
                                    >
                                      <KeyboardArrowDownIcon />
                                    </IconButton>
                                  </>
                                )}
                                <IconButton 
                                  size="small"
                                  onClick={() => removeVaultArticle(index)}
                                  sx={{ color: '#d32f2f' }}
                                  title="Delete Vault Article"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              label="Article Title"
                              value={article.title}
                              onChange={(e) => updateVaultArticle(index, 'title', e.target.value)}
                              placeholder="Enter article title"
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              label="Article URL"
                              value={article.url}
                              onChange={(e) => updateVaultArticle(index, 'url', e.target.value)}
                              placeholder="https://example.com/article"
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                              <TextField
                                fullWidth
                                label="Image URL"
                                value={article.imageUrl}
                                onChange={(e) => updateVaultArticle(index, 'imageUrl', e.target.value)}
                                placeholder="https://example.com/image.jpg"
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                      borderColor: themeMode === 'light' ? '#ccc' : '#666',
                                    },
                                    '&:hover fieldset': {
                                      borderColor: themeMode === 'light' ? '#999' : '#888',
                                    },
                                    '&.Mui-focused fieldset': {
                                      borderColor: '#b71c1c',
                                    },
                                    '& .MuiInputBase-input': {
                                      color: themeMode === 'light' ? '#333' : 'white',
                                    },
                                  },
                                  '& .MuiInputLabel-root': {
                                    color: themeMode === 'light' ? '#333' : 'white',
                                  },
                                  '& .MuiInputLabel-root.Mui-focused': {
                                    color: '#b71c1c',
                                  },
                                }}
                              />
                              <IconButton
                                onClick={() => handleOpenFileManager('vaultImage', index)}
                                sx={{ 
                                  color: '#d32f2f', 
                                  mt: 1,
                                  '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.1)' }
                                }}
                                title="Select Image from File Manager"
                              >
                                <FolderOpenIcon />
                              </IconButton>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    ))}
                  </Box>
                );
              }
               else if (sectionType === 'latest') {
                return (
                  <Box key="latest">
                    {sectionIndex > 0 && <Divider sx={{ my: 4 }} />}
                    {/* Latest Articles Section */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Checkbox
                          checked={newNewsletter.latestArticlesEnabled}
                          onChange={(e) => {
                            const newValue = e.target.checked;
                            setNewNewsletter(prev => ({ ...prev, latestArticlesEnabled: newValue }));
                          }}
                          sx={{ color: themeMode === 'dark' ? 'white' : '#d32f2f', mr: 1 }}
                        />
                        <Typography variant="h6" sx={{ color: themeMode === 'dark' ? 'white' : '#b71c1c' }}>Latest Articles</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <IconButton
                          size="small"
                          onClick={() => moveSectionUp('latest')}
                          disabled={sectionIndex === 0}
                          sx={{ color: '#d32f2f' }}
                          title="Move Section Up"
                        >
                          <KeyboardArrowUpIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => moveSectionDown('latest')}
                          disabled={sectionIndex === sectionOrder.length - 1}
                          sx={{ color: '#d32f2f' }}
                          title="Move Section Down"
                        >
                          <KeyboardArrowDownIcon />
                        </IconButton>
                      </Box>
                    </Box>
                    {/* Latest Articles - just a checkbox, no individual articles */}
                  </Box>
                );
              } else if (sectionType === 'recommended') {
                return (
                  <Box key="recommended">
                    {sectionIndex > 0 && <Divider sx={{ my: 4 }} />}
                    {/* Recommended For You Section */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Checkbox
                          checked={newNewsletter.recommendedArticlesEnabled}
                          onChange={(e) => {
                            const newValue = e.target.checked;
                            setNewNewsletter(prev => ({ ...prev, recommendedArticlesEnabled: newValue }));
                          }}
                          sx={{ color: themeMode === 'dark' ? 'white' : '#d32f2f', mr: 1 }}
                        />
                        <Typography variant="h6" sx={{ color: themeMode === 'dark' ? 'white' : '#b71c1c' }}>Recommended For You</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <IconButton
                          size="small"
                          onClick={() => moveSectionUp('recommended')}
                          disabled={sectionIndex === 0}
                          sx={{ color: '#d32f2f' }}
                          title="Move Section Up"
                        >
                          <KeyboardArrowUpIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => moveSectionDown('recommended')}
                          disabled={sectionIndex === sectionOrder.length - 1}
                          sx={{ color: '#d32f2f' }}
                          title="Move Section Down"
                        >
                          <KeyboardArrowDownIcon />
                        </IconButton>
                      </Box>
                    </Box>
                    {/* Recommended For You - just a checkbox, no individual articles */}
                  </Box>
                );
              } else if (sectionType === 'youMayAlsoLike') {
                return (
                  <Box key="youMayAlsoLike">
                    {sectionIndex > 0 && <Divider sx={{ my: 4 }} />}
                    {/* You May Also Like Section */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Checkbox
                          checked={newNewsletter.youMayAlsoLikeArticlesEnabled}
                          onChange={(e) => {
                            const newValue = e.target.checked;
                            setNewNewsletter(prev => ({ ...prev, youMayAlsoLikeArticlesEnabled: newValue }));
                          }}
                          sx={{ color: themeMode === 'dark' ? 'white' : '#d32f2f', mr: 1 }}
                        />
                        <Typography variant="h6" sx={{ color: themeMode === 'dark' ? 'white' : '#b71c1c' }}>You May Also Like</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <IconButton
                          size="small"
                          onClick={() => moveSectionUp('youMayAlsoLike')}
                          disabled={sectionIndex === 0}
                          sx={{ color: '#d32f2f' }}
                          title="Move Section Up"
                        >
                          <KeyboardArrowUpIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => moveSectionDown('youMayAlsoLike')}
                          disabled={sectionIndex === sectionOrder.length - 1}
                          sx={{ color: '#d32f2f' }}
                          title="Move Section Down"
                        >
                          <KeyboardArrowDownIcon />
                        </IconButton>
                      </Box>
                    </Box>
                    {/* You May Also Like - just a checkbox, no individual articles */}
                  </Box>
                );
              } else if (sectionType === 'trending') {
                return (
                  <Box key="trending">
                    {sectionIndex > 0 && <Divider sx={{ my: 4 }} />}
                    {/* Trending Articles Section */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Checkbox
                          checked={newNewsletter.trendingArticlesEnabled}
                          onChange={(e) => {
                            const newValue = e.target.checked;
                            setNewNewsletter(prev => ({ ...prev, trendingArticlesEnabled: newValue }));
                          }}
                          sx={{ color: themeMode === 'dark' ? 'white' : '#d32f2f', mr: 1 }}
                        />
                        <Typography variant="h6" sx={{ color: themeMode === 'dark' ? 'white' : '#b71c1c' }}>Trending Articles</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <IconButton
                          size="small"
                          onClick={() => moveSectionUp('trending')}
                          disabled={sectionIndex === 0}
                          sx={{ color: '#d32f2f' }}
                          title="Move Section Up"
                        >
                          <KeyboardArrowUpIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => moveSectionDown('trending')}
                          disabled={sectionIndex === sectionOrder.length - 1}
                          sx={{ color: '#d32f2f' }}
                          title="Move Section Down"
                        >
                          <KeyboardArrowDownIcon />
                        </IconButton>
                      </Box>
                    </Box>
                    {/* Trending Articles - just a checkbox, no individual articles */}
                  </Box>
                );
              } else if (sectionType === 'htmlBlock') {
                return (
                  <Box key="htmlBlock">
                    {sectionIndex > 0 && <Divider sx={{ my: 4 }} />}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Checkbox
                          checked={newNewsletter.htmlBlockEnabled}
                          onChange={(e) => setNewNewsletter(prev => ({ ...prev, htmlBlockEnabled: e.target.checked }))}
                          sx={{ color: themeMode === 'dark' ? 'white' : '#d32f2f', mr: 1 }}
                        />
                        <Typography variant="h6" sx={{ color: themeMode === 'dark' ? 'white' : '#b71c1c' }}>HTML Block</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <IconButton size="small" onClick={() => moveSectionUp('htmlBlock')} disabled={sectionIndex === 0} sx={{ color: '#d32f2f' }} title="Move Section Up">
                          <KeyboardArrowUpIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => moveSectionDown('htmlBlock')} disabled={sectionIndex === sectionOrder.length - 1} sx={{ color: '#d32f2f' }} title="Move Section Down">
                          <KeyboardArrowDownIcon />
                        </IconButton>
                      </Box>
                    </Box>
                    <TextField
                      fullWidth
                      multiline
                      minRows={10}
                      label="HTML Content"
                      value={newNewsletter.htmlBlock}
                      onChange={(e) => setNewNewsletter(prev => ({ ...prev, htmlBlock: e.target.value }))}
                      placeholder="Paste the HTML content for the review re-engagement email..."
                    />
                  </Box>
                );
              } else if (sectionType === 'textContent') {
                return (
                  <Box key="textContent">
                    {sectionIndex > 0 && <Divider sx={{ my: 4 }} />}
                    {/* Text Content Section */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Checkbox
                          checked={newNewsletter.textContentEnabled}
                          onChange={(e) => {
                            const newValue = e.target.checked;
                            setNewNewsletter(prev => ({ ...prev, textContentEnabled: newValue }));
                          }}
                          sx={{ color: themeMode === 'dark' ? 'white' : '#d32f2f', mr: 1 }}
                        />
                        <Typography variant="h6" sx={{ color: themeMode === 'dark' ? 'white' : '#b71c1c' }}>Text Content</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <IconButton
                          size="small"
                          onClick={() => moveSectionUp('textContent')}
                          disabled={sectionIndex === 0}
                          sx={{ color: '#d32f2f' }}
                          title="Move Section Up"
                        >
                          <KeyboardArrowUpIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => moveSectionDown('textContent')}
                          disabled={sectionIndex === sectionOrder.length - 1}
                          sx={{ color: '#d32f2f' }}
                          title="Move Section Down"
                        >
                          <KeyboardArrowDownIcon />
                        </IconButton>
                      </Box>
                    </Box>
                    <Box sx={{ p: 3, border: themeMode === 'light' ? '1px solid #ddd' : '1px solid #444', borderRadius: 2, bgcolor: themeMode === 'light' ? '#fafafa' : '#2a2a2a' }}>
                      <TextField
                        fullWidth
                        multiline
                        rows={6}
                        label="Text Content"
                        value={newNewsletter.textContent}
                        onChange={(e) => setNewNewsletter({ ...newNewsletter, textContent: e.target.value })}
                        placeholder="Enter custom text content for the newsletter..."
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: themeMode === 'light' ? '#ccc' : '#666',
                            },
                            '&:hover fieldset': {
                              borderColor: themeMode === 'light' ? '#999' : '#888',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#b71c1c',
                            },
                            '& .MuiInputBase-input': {
                              color: themeMode === 'light' ? '#333' : 'white',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: themeMode === 'light' ? '#333' : 'white',
                          },
                          '& .MuiInputLabel-root.Mui-focused': {
                            color: '#b71c1c',
                          },
                        }}
                      />
                    </Box>
                  </Box>
                );
              } else if (sectionType === 'trendingVideos') {
                return (
                  <Box key="trendingVideos">
                    {sectionIndex > 0 && <Divider sx={{ my: 4 }} />}
                    {/* Trending Videos Section */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Checkbox
                          checked={newNewsletter.trendingVideosEnabled}
                          onChange={(e) => {
                            const newValue = e.target.checked;
                            setNewNewsletter(prev => ({ ...prev, trendingVideosEnabled: newValue }));
                          }}
                          sx={{ color: themeMode === 'dark' ? 'white' : '#d32f2f', mr: 1 }}
                        />
                        <Typography variant="h6" sx={{ color: themeMode === 'dark' ? 'white' : '#b71c1c' }}>Trending Videos</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <IconButton
                          size="small"
                          onClick={() => moveSectionUp('trendingVideos')}
                          disabled={sectionIndex === 0}
                          sx={{ color: '#d32f2f' }}
                          title="Move Section Up"
                        >
                          <KeyboardArrowUpIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => moveSectionDown('trendingVideos')}
                          disabled={sectionIndex === sectionOrder.length - 1}
                          sx={{ color: '#d32f2f' }}
                          title="Move Section Down"
                        >
                          <KeyboardArrowDownIcon />
                        </IconButton>
                      </Box>
                    </Box>
                    {/* Trending Videos - just a checkbox, no individual articles */}
                  </Box>
                );
              }
              return null;
            })}
          </Box>
        );
      }

      case 2: // Ads
        if (isCarAndDriverSyncSelected) {
          return null;
        }
        return (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ color: themeMode === 'dark' ? 'white' : '#b71c1c' }}>Advertisement Configuration</Typography>
              <IconButton 
                onClick={addAd} 
                sx={{ 
                  color: '#d32f2f', 
                  '&:hover': { 
                    color: '#b71c1c',
                    bgcolor: 'rgba(211, 47, 47, 0.1)'
                  } 
                }}
                title="Add Advertisement"
              >
                <AddIcon />
              </IconButton>
            </Box>
            {newNewsletter.ads.map((ad, index) => (
              <Box key={index} sx={{ mb: 3, p: 3, border: themeMode === 'light' ? '1px solid #ddd' : '1px solid #444', borderRadius: 2, bgcolor: themeMode === 'light' ? '#fafafa' : '#2a2a2a' }}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: themeMode === 'dark' ? 'white' : 'inherit' }}>Ad {index + 1}</Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {newNewsletter.ads.length > 1 && (
                          <>
                            <IconButton
                              size="small"
                              onClick={() => moveUpAd(index)}
                              disabled={index === 0}
                              sx={{ color: '#d32f2f' }}
                              title="Move Up"
                            >
                              <KeyboardArrowUpIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => moveDownAd(index)}
                              disabled={index === newNewsletter.ads.length - 1}
                              sx={{ color: '#d32f2f' }}
                              title="Move Down"
                            >
                              <KeyboardArrowDownIcon />
                            </IconButton>
                          </>
                        )}
                        <IconButton 
                          size="small"
                          onClick={() => removeAd(index)}
                          sx={{ color: '#d32f2f' }}
                          title="Delete Ad"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                      <TextField
                        fullWidth
                        label="Image URL"
                        value={ad.imageUrl}
                        onChange={(e) => updateAd(index, 'imageUrl', e.target.value)}
                        placeholder="https://example.com/ad-image.jpg"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: themeMode === 'light' ? '#ccc' : '#666',
                            },
                            '&:hover fieldset': {
                              borderColor: themeMode === 'light' ? '#999' : '#888',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#b71c1c',
                            },
                            '& .MuiInputBase-input': {
                              color: themeMode === 'light' ? '#333' : 'white',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: themeMode === 'light' ? '#333' : 'white',
                          },
                          '& .MuiInputLabel-root.Mui-focused': {
                            color: '#b71c1c',
                          },
                        }}
                      />
                      <IconButton
                        onClick={() => handleOpenFileManager('adImage', index)}
                        sx={{ 
                          color: '#d32f2f', 
                          mt: 1,
                          '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.1)' }
                        }}
                        title="Select Image from File Manager"
                      >
                        <FolderOpenIcon />
                      </IconButton>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Ad Link"
                      value={ad.adLink}
                      onChange={(e) => updateAd(index, 'adLink', e.target.value)}
                      placeholder="https://example.com/ad-link"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Position"
                      value={ad.position}
                      onChange={(e) => updateAd(index, 'position', parseInt(e.target.value))}
                      inputProps={{ min: 1, max: 10 }}
                      placeholder="1-10"
                    />
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Box>
        );

      case 3: // Footer
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, color: themeMode === 'dark' ? 'white' : '#b71c1c' }}>Footer Configuration</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Footer Text"
                  value={newNewsletter.footerText}
                  onChange={(e) => setNewNewsletter({ ...newNewsletter, footerText: e.target.value })}
                  placeholder="Enter footer text content..."
                />
              </Grid>
              {newNewsletter.footerLinks.map((link, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <TextField
                    fullWidth
                    label={`${link.linkText} URL`}
                    value={link.linkURL}
                    onChange={(e) => updateFooterLink(index, 'linkURL', e.target.value)}
                    placeholder={`https://example.com/${link.linkText.toLowerCase().replace(/\s+/g, '-')}`}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        );

      case 4: // Schedule
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, color: themeMode === 'dark' ? 'white' : '#b71c1c' }}>Scheduling Configuration</Typography>
            <Grid container spacing={3}>
              {isReEngagementAudienceValue(newNewsletter.audience) && (
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label={newNewsletter.audience === 'automate_rating_reengagement_email'
                      ? "Days after signup"
                      : "Eligibility Window (days)"}
                    helperText={newNewsletter.audience === 'automate_rating_reengagement_email'
                      ? "Send the first email once signup was at least this many days ago, for users who signed up on or after August 8, 2026. If a vehicle is added later, eligibility still uses the original signup date. Eligible users remain due until the email is sent."
                      : newNewsletter.audience === 'automate_vehicle_selection_reengagement_email'
                        ? "Send the first Add Your Vehicle email to eligible users who signed up on or after June 1, 2026. Eligibility is based on the original signup date."
                      : isReviewReEngagementAudienceValue(newNewsletter.audience)
                        ? "Send the first review follow-up this many days after rating submission. Only ratings included by the UGC sync are eligible."
                        : "Send to users whose signup or vehicle selection was this many days ago."}
                    value={newNewsletter.frequencyWindowDays}
                    onChange={(e) => setNewNewsletter({
                      ...newNewsletter,
                      frequencyWindowDays: parseInt(e.target.value, 10) || 3
                    })}
                    inputProps={{ min: 1, max: 30 }}
                  />
                </Grid>
              )}
              {!isDailyOnlyReEngagementAudienceValue(newNewsletter.audience) && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={newNewsletter.schedule.enabled}
                        onChange={(e) => setNewNewsletter({
                          ...newNewsletter,
                          schedule: { ...newNewsletter.schedule, enabled: e.target.checked }
                        })}
                      />
                    }
                    label="Enable Newsletter Scheduling"
                  />
                </Grid>
              )}
              {!isDailyOnlyReEngagementAudienceValue(newNewsletter.audience) && newNewsletter.schedule.enabled && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>Schedule Time:</Typography>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Minute"
                      value={newNewsletter.schedule.minute}
                      onChange={(e) => setNewNewsletter({
                        ...newNewsletter,
                        schedule: { ...newNewsletter.schedule, minute: parseInt(e.target.value) }
                      })}
                      inputProps={{ min: 0, max: 59 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Hour"
                      value={newNewsletter.schedule.hour}
                      onChange={(e) => setNewNewsletter({
                        ...newNewsletter,
                        schedule: { ...newNewsletter.schedule, hour: parseInt(e.target.value) }
                      })}
                      inputProps={{ min: 0, max: 23 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Day"
                      value={newNewsletter.schedule.day}
                      onChange={(e) => setNewNewsletter({
                        ...newNewsletter,
                        schedule: { ...newNewsletter.schedule, day: parseInt(e.target.value) }
                      })}
                      inputProps={{ min: 1, max: 31 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Month"
                      value={newNewsletter.schedule.month}
                      onChange={(e) => setNewNewsletter({
                        ...newNewsletter,
                        schedule: { ...newNewsletter.schedule, month: parseInt(e.target.value) }
                      })}
                      inputProps={{ min: 1, max: 12 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      label="Weekdays"
                      value={newNewsletter.schedule.weekdays.length > 0 ? newNewsletter.schedule.weekdays.length.toString() : '?'}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || value === '?') {
                          setNewNewsletter({
                            ...newNewsletter,
                            schedule: { ...newNewsletter.schedule, weekdays: [] }
                          });
                          return;
                        }
                        const count = parseInt(value) || 0;
                        const defaultWeekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                        const selectedWeekdays = defaultWeekdays.slice(0, count);
                        setNewNewsletter({
                          ...newNewsletter,
                          schedule: { ...newNewsletter.schedule, weekdays: selectedWeekdays }
                        });
                      }}
                      placeholder="?"
                      sx={{
                        '& .MuiInputLabel-root': {
                          color: themeMode === 'light' ? '#333' : 'white'
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#b71c1c'
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Year"
                      value={newNewsletter.schedule.year}
                      onChange={(e) => setNewNewsletter({
                        ...newNewsletter,
                        schedule: { ...newNewsletter.schedule, year: parseInt(e.target.value) }
                      })}
                      inputProps={{ min: new Date().getFullYear() }}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        );

      default:
        return null;
    }
  };

  const moveSectionUp = (sectionType: 'featured' | 'vault' | 'latest' | 'recommended' | 'youMayAlsoLike' | 'trending' | 'textContent' | 'htmlBlock' | 'trendingVideos') => {
    const currentIndex = sectionOrder.indexOf(sectionType);
    if (currentIndex > 0) {
      const newOrder = [...sectionOrder];
      [newOrder[currentIndex], newOrder[currentIndex - 1]] = [newOrder[currentIndex - 1], newOrder[currentIndex]];
      setSectionOrder(newOrder);
    }
  };

  const moveSectionDown = (sectionType: 'featured' | 'vault' | 'latest' | 'recommended' | 'youMayAlsoLike' | 'trending' | 'textContent' | 'htmlBlock' | 'trendingVideos') => {
    const currentIndex = sectionOrder.indexOf(sectionType);
    if (currentIndex < sectionOrder.length - 1) {
      const newOrder = [...sectionOrder];
      [newOrder[currentIndex], newOrder[currentIndex + 1]] = [newOrder[currentIndex + 1], newOrder[currentIndex]];
      setSectionOrder(newOrder);
    }
  };

  // File Manager handlers
  const handleOpenFileManager = (type: 'headerLogo' | 'articleImage' | 'vaultImage' | 'adImage', index?: number) => {
    setFileManagerTarget({ type, index });
    setFileManagerOpen(true);
  };

  const handleFileSelect = (fileUrl: string, fileName: string) => {
    if (!fileManagerTarget) return;

    switch (fileManagerTarget.type) {
      case 'headerLogo':
        setNewNewsletter(prev => ({ ...prev, headerLogo: fileUrl }));
        break;
      case 'articleImage':
        if (fileManagerTarget.index !== undefined) {
          const updatedArticles = [...newNewsletter.articles];
          updatedArticles[fileManagerTarget.index] = { 
            ...updatedArticles[fileManagerTarget.index], 
            imageUrl: fileUrl 
          };
          setNewNewsletter(prev => ({ ...prev, articles: updatedArticles }));
        }
        break;
      case 'vaultImage':
        if (fileManagerTarget.index !== undefined) {
          const updatedVaultArticles = [...newNewsletter.vaultArticles];
          updatedVaultArticles[fileManagerTarget.index] = { 
            ...updatedVaultArticles[fileManagerTarget.index], 
            imageUrl: fileUrl 
          };
          setNewNewsletter(prev => ({ ...prev, vaultArticles: updatedVaultArticles }));
        }
        break;
      case 'adImage':
        if (fileManagerTarget.index !== undefined) {
          const updatedAds = [...newNewsletter.ads];
          updatedAds[fileManagerTarget.index] = { 
            ...updatedAds[fileManagerTarget.index], 
            imageUrl: fileUrl 
          };
          setNewNewsletter(prev => ({ ...prev, ads: updatedAds }));
        }
        break;
    }
  };

  const handleCloseFileManager = () => {
    setFileManagerOpen(false);
    setFileManagerTarget(null);
  };

  const handlePreviewDraft = () => {
    // For editing existing newsletter, use the newsletter ID
    if (isEditing && editingNewsletterId) {
      const previewUrl = `${apiUrl}/prod/get-newsletter?entryId=${editingNewsletterId}`;
      window.open(previewUrl, '_blank');
    } else {
      // For new newsletter, we need to save it as draft first, then preview
      // This is a temporary solution - in a real app, you might want to create a preview endpoint
      // that accepts the form data directly without saving
      alert('Please save the newsletter first to preview it. Use "Save" button to create a draft, then you can preview it.');
    }
  };

  const handleMoveToFolder = async (newsletterId: string) => {
    setNewsletterToMove(newsletterId);
    // Fetch available folders
    try {
      const token = getAuthToken();
      const response = await fetch(`${apiUrl}/prod/newsletter-folders`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch folders');
      const data = await response.json();
      
      // Flatten folder structure for the dropdown
      const flattenFolders = (folders: any[], prefix = ''): any[] => {
        let result: any[] = [];
        folders.forEach(folder => {
          result.push({ ...folder, displayName: prefix + folder.name });
          if (folder.subfolders && folder.subfolders.length > 0) {
            result = result.concat(flattenFolders(folder.subfolders, prefix + folder.name + ' / '));
          }
        });
        return result;
      };
      
      setAvailableFolders(flattenFolders(data.folders || []));
      setMoveFolderDialog(true);
    } catch (err: any) {
      setError('Failed to fetch folders');
    }
  };

  const handleMoveFolderConfirm = async () => {
    if (!newsletterToMove) return;

    try {
      const token = getAuthToken();
      
      const response = await fetch(`${apiUrl}/prod/newsletter-management/${newsletterToMove}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          folderId: selectedDestinationFolder || null,
          updatedAt: new Date().toISOString()
        })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to move newsletter');
      }

      setSuccessMessage('Newsletter moved successfully');
      setMoveFolderDialog(false);
      setNewsletterToMove(null);
      setSelectedDestinationFolder('');
      
      // Refresh the newsletter list
      await fetchNewsletters();
      if (onNewsletterCreated) {
        onNewsletterCreated();
      }
      
    } catch (err: any) {
      setError(err.message || 'Failed to move newsletter');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <Typography>Loading newsletters...</Typography>
      </Box>
    );
  }

  const isCarAndDriverSyncSelected = isCarAndDriverSyncAudienceValue(newNewsletter.audience);
  const visibleTabs = isCarAndDriverSyncSelected
    ? NEWSLETTER_FORM_TABS.filter((tab) => tab.value === 4)
    : NEWSLETTER_FORM_TABS;
  const selectedTabValue = visibleTabs.some((tab) => tab.value === activeTab)
    ? activeTab
    : visibleTabs[0].value;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ color: themeMode === 'light' ? '#333' : 'white' }}>
          {selectedFolderName ? `📁 ${selectedFolderName}` : 'Newsletters'}
        </Typography>
        <Tooltip title="Add New Newsletter">
          <Fab 
            color="primary" 
            size="small"
            onClick={handleAdd}
            sx={{ bgcolor: '#d32f2f', '&:hover': { bgcolor: '#b71c1c' } }}
          >
            <AddIcon />
          </Fab>
        </Tooltip>
      </Box>

      {/* Folder Context Info */}
      {selectedFolder && (
        <Box sx={{ mb: 3, p: 2, bgcolor: themeMode === 'light' ? '#f5f5f5' : '#424242', borderRadius: 1 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Showing {newsletters.length} newsletter{newsletters.length !== 1 ? 's' : ''} in this folder
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={() => setSuccessMessage('')}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {successMessage}
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ 
        bgcolor: themeMode === 'light' ? 'white' : '#232323',
        position: 'relative',
        boxShadow: themeMode === 'light' 
          ? '0 4px 20px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1)' 
          : '0 4px 20px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.3)'
      }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ 
                color: themeMode === 'light' ? '#333' : 'white',
                borderBottom: themeMode === 'light' ? '1px solid #ccc' : '1px solid #444',
                fontWeight: 'bold',
                padding: '8px 16px',
                fontSize: '0.875rem'
              }}>Title</TableCell>
              <TableCell sx={{ 
                color: themeMode === 'light' ? '#333' : 'white',
                borderBottom: themeMode === 'light' ? '1px solid #ccc' : '1px solid #444',
                fontWeight: 'bold',
                padding: '8px 16px',
                fontSize: '0.875rem'
              }}>Author</TableCell>
              <TableCell sx={{ 
                color: themeMode === 'light' ? '#333' : 'white',
                borderBottom: themeMode === 'light' ? '1px solid #ccc' : '1px solid #444',
                fontWeight: 'bold',
                padding: '8px 16px',
                fontSize: '0.875rem'
              }}>Status</TableCell>
              <TableCell sx={{ 
                color: themeMode === 'light' ? '#333' : 'white',
                borderBottom: themeMode === 'light' ? '1px solid #ccc' : '1px solid #444',
                fontWeight: 'bold',
                padding: '8px 16px',
                fontSize: '0.875rem'
              }}>Created</TableCell>
              <TableCell sx={{ 
                color: themeMode === 'light' ? '#333' : 'white',
                borderBottom: themeMode === 'light' ? '1px solid #ccc' : '1px solid #444',
                fontWeight: 'bold',
                padding: '8px 16px',
                fontSize: '0.875rem'
              }}>Modified</TableCell>
              <TableCell sx={{ 
                color: themeMode === 'light' ? '#333' : 'white',
                borderBottom: themeMode === 'light' ? '1px solid #ccc' : '1px solid #444',
                fontWeight: 'bold',
                padding: '8px 16px',
                fontSize: '0.875rem'
              }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {newsletters.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ 
                  color: themeMode === 'light' ? '#333' : 'white', 
                  textAlign: 'center',
                  borderBottom: themeMode === 'light' ? '1px solid #ccc' : '1px solid #444',
                  padding: '8px 16px',
                  fontSize: '0.875rem'
                }}>
                  No newsletters found
                </TableCell>
              </TableRow>
            ) : (
              newsletters.map((newsletter) => (
                <TableRow key={newsletter.id} sx={{
                  '&:hover': {
                    bgcolor: themeMode === 'light' ? '#f0f0f0' : 'rgba(255, 255, 255, 0.08)'
                  }
                }}>
                  <TableCell sx={{ 
                    color: themeMode === 'light' ? '#333' : 'white',
                    borderBottom: themeMode === 'light' ? '1px solid #ccc' : '1px solid #444',
                    padding: '8px 16px',
                    fontSize: '0.875rem'
                  }}>{newsletter.title}</TableCell>
                  <TableCell sx={{ 
                    color: themeMode === 'light' ? '#333' : 'white',
                    borderBottom: themeMode === 'light' ? '1px solid #ccc' : '1px solid #444',
                    padding: '8px 16px',
                    fontSize: '0.875rem'
                  }}>{newsletter.author}</TableCell>
                  <TableCell sx={{
                    borderBottom: themeMode === 'light' ? '1px solid #ccc' : '1px solid #444',
                    padding: '8px 16px'
                  }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: '0.75rem',
                        textTransform: 'capitalize',
                        bgcolor: newsletter.status === 'published' ? '#4caf50' : 
                                newsletter.status === 'draft' ? '#ff9800' : '#9e9e9e',
                        color: 'white',
                        px: 1,
                        py: 0.5,
                        borderRadius: '12px',
                        fontWeight: 'medium',
                        display: 'inline-block'
                      }}
                    >
                      {newsletter.status}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ 
                    color: themeMode === 'light' ? '#333' : 'white',
                    borderBottom: themeMode === 'light' ? '1px solid #ccc' : '1px solid #444',
                    padding: '8px 16px',
                    fontSize: '0.875rem'
                  }}>
                    {new Date(newsletter.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell sx={{ 
                    color: themeMode === 'light' ? '#333' : 'white',
                    borderBottom: themeMode === 'light' ? '1px solid #ccc' : '1px solid #444',
                    padding: '8px 16px',
                    fontSize: '0.875rem'
                  }}>
                    {new Date(newsletter.updatedAt).toLocaleString()}
                  </TableCell>
                  <TableCell sx={{
                    borderBottom: themeMode === 'light' ? '1px solid #ccc' : '1px solid #444',
                    padding: '8px 16px'
                  }}>
                    {newsletter.status === 'published' &&
                      (getNewsletterTypeFromAudience(newsletter.audienceVariation || newsletter.newsletterType || '') === 'automate_rating_reengagement_email' ||
                        getNewsletterTypeFromAudience(newsletter.audienceVariation || newsletter.newsletterType || '') === 'automate_review_reengagement_email') && (
                        <IconButton
                          onClick={() => handleManualAutomatedCampaignRun(newsletter)}
                          sx={{ color: '#6a1b9a', mr: 1 }}
                          title="Run Automated Campaign Now"
                          aria-label="Run Automated Campaign Now"
                          size="small"
                          disabled={triggeringCampaignId === newsletter.id}
                        >
                          <PlayArrowIcon fontSize="small" />
                        </IconButton>
                      )}
                    <IconButton 
                      onClick={() => handleEdit(newsletter.id)} 
                      sx={{ color: '#2196f3', mr: 1 }}
                      title="Edit Newsletter"
                      size="small"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      onClick={() => handleCopy(newsletter.id)} 
                      sx={{ color: '#4caf50', mr: 1 }}
                      title="Copy Newsletter"
                      size="small"
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      onClick={() => handleMoveToFolder(newsletter.id)} 
                      sx={{ color: '#ff9800', mr: 1 }}
                      title="Move to Folder"
                      size="small"
                    >
                      <DriveFileMoveIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      onClick={() => handleDelete(newsletter.id)} 
                      sx={{ color: '#d32f2f' }}
                      title="Delete Newsletter"
                      size="small"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Newsletter Dialog */}
      <Dialog 
        open={createDialog} 
        onClose={(event, reason) => {
          const shouldKeepOpen = keepDialogOpenRef.current || keepDialogOpen;
          if (!shouldKeepOpen) {
            closeCreateDialog(true);
          }
        }}
        maxWidth="lg" 
        fullWidth
        disableEscapeKeyDown={keepDialogOpenRef.current || keepDialogOpen}
      >
        <DialogTitle>{isEditing ? 'Edit Newsletter' : 'Create New Newsletter'}</DialogTitle>
        <DialogContent sx={{ maxHeight: '80vh', overflow: 'auto' }}>
          {/* Basic Information Section */}
          <Box sx={{ 
            mb: 3, 
            p: 3, 
            bgcolor: themeMode === 'light' ? 'white' : '#232323', 
            borderRadius: 2,
            border: themeMode === 'light' ? '2px solid #ddd' : '2px solid #444'
          }}>
            <Typography variant="h6" sx={{ mb: 3, color: themeMode === 'dark' ? 'white' : '#b71c1c', fontWeight: 'bold' }}>
              Basic Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: themeMode === 'light' ? '#333' : 'white' }}>Audience</InputLabel>
                  <Select
                    value={newNewsletter.audience}
                    label="Audience"
                    onChange={(e) => handleAudienceChange(e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: themeMode === 'light' ? '#ccc' : '#666',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: themeMode === 'light' ? '#999' : '#888',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#b71c1c',
                      },
                      '& .MuiSelect-icon': {
                        color: themeMode === 'light' ? '#333' : 'white',
                      },
                      '& .MuiInputBase-input': {
                        color: themeMode === 'light' ? '#333' : 'white',
                      },
                    }}
                  >
                    {getAudienceOptions().map((audience) => (
                      <MenuItem key={audience} value={audience}>{audience}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Newsletter Name"
                  value={newNewsletter.newsletterName}
                  onChange={(e) => setNewNewsletter({ ...newNewsletter, newsletterName: e.target.value })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: themeMode === 'light' ? '#ccc' : '#666',
                      },
                      '&:hover fieldset': {
                        borderColor: themeMode === 'light' ? '#999' : '#888',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#b71c1c',
                      },
                      '& .MuiInputBase-input': {
                        color: themeMode === 'light' ? '#333' : 'white',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: themeMode === 'light' ? '#333' : 'white',
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#b71c1c',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Subject"
                  helperText="Email subject line shown to recipients."
                  value={newNewsletter.subject}
                  error={!!createError && !newNewsletter.subject.trim()}
                  onChange={(e) => setNewNewsletter({ ...newNewsletter, subject: e.target.value })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: themeMode === 'light' ? '#ccc' : '#666',
                      },
                      '&:hover fieldset': {
                        borderColor: themeMode === 'light' ? '#999' : '#888',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#b71c1c',
                      },
                      '& .MuiInputBase-input': {
                        color: themeMode === 'light' ? '#333' : 'white',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: themeMode === 'light' ? '#333' : 'white',
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#b71c1c',
                    },
                  }}
                />
              </Grid>
              {process.env.REACT_APP_SUBJECT_LINE_ENABLED === 'true' && !isCarAndDriverSyncSelected && (
              <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={newNewsletter.enableLlmSubjectLine}
                      onChange={(e) => setNewNewsletter({ ...newNewsletter, enableLlmSubjectLine: e.target.checked })}
                      color="primary"
                    />
                  }
                  label="AI-Generated Subject Line"
                />
                <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                  When enabled, personalized recipients receive an LLM-generated subject line based on their recommended articles.
                </Typography>
              </Grid>
              )}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Preview Text"
                  value={newNewsletter.previewText}
                  onChange={(e) => setNewNewsletter({ ...newNewsletter, previewText: e.target.value })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: themeMode === 'light' ? '#ccc' : '#666',
                      },
                      '&:hover fieldset': {
                        borderColor: themeMode === 'light' ? '#999' : '#888',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#b71c1c',
                      },
                      '& .MuiInputBase-input': {
                        color: themeMode === 'light' ? '#333' : 'white',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: themeMode === 'light' ? '#333' : 'white',
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#b71c1c',
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Tabs for other sections */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={selectedTabValue}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  minWidth: 120,
                  fontWeight: 500,
                  color: themeMode === 'dark' ? 'white' : '#666',
                  '&.Mui-selected': {
                    color: '#b71c1c',
                  },
                },
                '& .MuiTabs-indicator': {
                  bgcolor: '#b71c1c',
                },
              }}
            >
              {visibleTabs.map((tab) => (
                <Tab key={tab.value} value={tab.value} label={tab.label} />
              ))}
            </Tabs>
          </Box>

          {/* Tab Content */}
          <Box sx={{ mt: 2 }}>
            {renderTabContent(selectedTabValue)}
          </Box>

          {createError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {createError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => closeCreateDialog(true)} sx={{ color: '#d32f2f' }}>Cancel</Button>
          {!isCarAndDriverSyncSelected && (
            <Button
              onClick={handlePreviewDraft}
              variant="outlined"
              startIcon={<VisibilityIcon />}
              sx={{
                borderColor: '#2196f3',
                color: '#2196f3',
                '&:hover': {
                  borderColor: '#1976d2',
                  color: '#1976d2',
                  bgcolor: 'rgba(33, 150, 243, 0.1)'
                }
              }}
            >
              Preview
            </Button>
          )}
          <Button 
            type="button"
            onClick={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              await handleCreateNewsletter('draft');
            }}
            variant="contained" 
            sx={{ bgcolor: '#ff9800', '&:hover': { bgcolor: '#f57c00' } }}
          >
            {isEditing ? 'Save Changes' : 'Save'}
          </Button>
          <Button 
            type="button"
            onClick={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              await handleCreateNewsletter('published');
            }}
            variant="contained" 
            sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#388e3c' } }}
          >
            {isEditing ? 'Save and Publish' : 'Save and Publish'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* File Manager Modal */}
      <Dialog 
        open={fileManagerOpen} 
        onClose={handleCloseFileManager}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: themeMode === 'dark' ? '#2a2a2a' : 'white',
            color: themeMode === 'dark' ? 'white' : '#333',
            minHeight: '80vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: themeMode === 'dark' ? '1px solid #444' : '1px solid #ddd'
        }}>
          <Typography variant="h6">Select Image</Typography>
          <IconButton onClick={handleCloseFileManager} sx={{ color: themeMode === 'dark' ? 'white' : '#333' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <ImageGallery
            themeMode={themeMode}
            onSelectImage={(fileUrl, fileName) => {
              handleFileSelect(fileUrl, fileName);
              handleCloseFileManager();
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Move to Folder Dialog */}
      <Dialog open={moveFolderDialog} onClose={() => setMoveFolderDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Move Newsletter to Folder</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel sx={{ color: themeMode === 'light' ? '#333' : 'white' }}>Destination Folder</InputLabel>
            <Select
              value={selectedDestinationFolder}
              label="Destination Folder"
              onChange={(e) => setSelectedDestinationFolder(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: themeMode === 'light' ? '#ccc' : '#666',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: themeMode === 'light' ? '#999' : '#888',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#b71c1c',
                },
                '& .MuiSelect-icon': {
                  color: themeMode === 'light' ? '#333' : 'white',
                },
                '& .MuiInputBase-input': {
                  color: themeMode === 'light' ? '#333' : 'white',
                },
              }}
            >
              <MenuItem value="">
                <em>No Folder (Root)</em>
              </MenuItem>
              {availableFolders.map((folder) => (
                <MenuItem key={folder.id} value={folder.id}>
                  {folder.displayName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setMoveFolderDialog(false);
            setNewsletterToMove(null);
            setSelectedDestinationFolder('');
          }} sx={{ color: '#d32f2f' }}>
            Cancel
          </Button>
          <Button 
            onClick={handleMoveFolderConfirm} 
            variant="contained" 
            sx={{ bgcolor: '#ff9800', '&:hover': { bgcolor: '#f57c00' } }}
          >
            Move
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NewsletterManagement;
